<template>
  <ShotcutKeysModal
    :open-modal="shortcutModal"
    @closeModal="displayShortcutModal"
  />

  <Loading v-if="showLoading" />

  <OperationProgress
    v-if="embeddingProgress.length"
    :process-list="embeddingProgress"
    process-type="Image Embedding"
  />

  <a-modal
    v-model:visible="openSegmentModal"
    title="Preserve Segmentation"
    centered
    @ok="preserveSegmentationState"
    @cancel="closePreserveSegmentationModal"
  >
    <a-typography-title :level="4" class="ml-2 mt-2">
      Task Objects List
    </a-typography-title>
    <a-table
      class="mt-3"
      :columns="taskObjectListColumns"
      :data-source="annotateObjects"
      :row-selection="rowSelection"
      :row-key="'id'"
      :scroll="{
        y: '40vh',
        x: true,
      }"
      :pagination="false"
    >
      <template #emptyText>
        <div
          id="no-models-found"
          style="height: 35vh"
          class="d-flex flex-column align-items-center justify-content-center"
        >
          <laptop-outlined style="font-size: 40px" />
          <span class="mt-2">No Models Found</span>
        </div>
      </template>
    </a-table>
  </a-modal>

  <a-row
    class="h-100 d-flex align-items-center px-0 py-0 mx-0"
    :gutter="[24, 0]"
  >
    <a-col
      span="18"
      class="h-100 d-flex flex-column px-0 py-0"
      style="position: relative"
    >
      <div
        ref="floatingCard"
        style="width: 0; height: 0; background-color: white; position: absolute"
      >
        <div v-if="activeObjectArea" class="p-2">
          <div class="fw-semibold">Label Object</div>
          <form @submit.prevent="handleSave" action="">
            <div>
              <a-select
                class="my-3"
                ref="annotateSelect"
                v-model:value="annotate"
                show-search
                style="width: 100%"
                :size="'small'"
                :options="annotationObjectList"
                :filterOption="filterOption"
                :default-active-first-option="false"
                :allowClear="true"
                @search="handleSearchChange"
                :notFoundContent="null"
              ></a-select>
            </div>
            <div class="d-flex justify-content-end align-items-center my-1">
              <a-button
                :loading="creatingTask"
                @click="handleSave"
                type="primary"
                size="small"
                >Save</a-button
              >
            </div>
          </form>
        </div>
      </div>

      <a-card
        id="annotation-object"
        :body-style="{
          padding: '1em',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }"
        size="small"
        class="annotation-image-card d-flex flex-column flex-grow-1"
        style="position: relative"
      >
        <a-spin
          v-if="imageLoading"
          size="large"
          tip="Loading Image..."
          class="m-auto"
        />

        <a-result
          v-else-if="!imageLoading && !image"
          id="not-found-label-image"
          title="Image Not Found!"
          class="m-auto"
        >
          <template #icon>
            <exclamation-circle-outlined class="text-danger" />
          </template>
        </a-result>
        <template #title>
          <div class="card-title">
            <a-space direction="vertical" :size="20" style="text-align: center">
              <a-tooltip>
                <template #title>Draw Shape (W)</template>
                <a-button
                  id="region-modal-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :disabled="
                    imageLoading || isObjectFetching || enableSegmentation
                  "
                  :style="{
                    backgroundColor: isAddingRectangle ? 'blue' : 'white',
                    color: isAddingRectangle ? 'white' : 'blue',
                  }"
                  @click="setNewObjectConfigToDraw"
                >
                  <template #icon>
                    <border-outlined />
                  </template>
                </a-button>
              </a-tooltip>
              <a-tooltip placement="topLeft">
                <template #title>Apply Previous Labels (Ctrl + X)</template>
                <a-button
                  id="region-modal-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :disabled="
                    imageLoading ||
                    prevImageButtonDisabled ||
                    isApplyPreviousLabel
                  "
                  :style="
                    imageLoading ||
                    prevImageButtonDisabled ||
                    isApplyPreviousLabel
                      ? {}
                      : {
                          backgroundColor: isHistoryApplied ? 'blue' : 'white',
                          color: isHistoryApplied ? 'white' : 'blue',
                        }
                  "
                  @click="renderLastExistingObjectList"
                >
                  <template #icon>
                    <HistoryOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip placement="topLeft">
                <template #title>Zoom In (Ctrl + Alt + +)</template>
                <a-button
                  id="zoom-in-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :disabled="imageLoading"
                  :style="{
                    backgroundColor: 'white',
                    color: 'blue',
                  }"
                  @click="handleLayerZoomIn"
                >
                  <template #icon>
                    <ZoomInOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip placement="topLeft">
                <template #title>Zoom Out (Ctrl + Alt + -)</template>
                <a-button
                  id="zoom-in-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :style="
                    layerScale <= 1
                      ? {}
                      : {
                          backgroundColor: 'white',
                          color: 'blue',
                        }
                  "
                  :disabled="layerScale <= 1"
                  @click="handleLayerZoomOut"
                >
                  <template #icon>
                    <ZoomOutOutlined />
                  </template>
                </a-button>
              </a-tooltip>
              <a-popover
                placement="right"
                trigger="click"
                v-model:visible="isPopoverVisible"
              >
                <template #content>
                  <!-- Container for buttons with vertical layout -->
                  <div style="display: flex; flex-direction: column; gap: 8px">
                    <a-tooltip placement="topLeft">
                      <template #title>Delete (delete)</template>
                      <a-button
                        id="zoom-in-btn"
                        shape="circle"
                        type="primary"
                        :size="'large'"
                        :style="{
                          backgroundColor: 'white',
                          color: 'blue',
                        }"
                        @click="deleteObject"
                      >
                        <template #icon>
                          <DeleteOutlined />
                        </template>
                      </a-button>
                    </a-tooltip>

                    <a-tooltip placement="topLeft">
                      <template #title>Copy (Ctrl + C)</template>
                      <a-button
                        id="zoom-in-btn"
                        shape="circle"
                        type="primary"
                        :size="'large'"
                        :style="{
                          backgroundColor: 'white',
                          color: 'blue',
                        }"
                        @click="copySelectedShapes"
                      >
                        <template #icon>
                          <CopyOutlined />
                        </template>
                      </a-button>
                    </a-tooltip>
                  </div>
                </template>

                <a-tooltip placement="topLeft">
                  <template #title>Select All (Ctrl + Q)</template>
                  <a-button
                    id="zoom-in-btn"
                    shape="circle"
                    type="primary"
                    :size="'large'"
                    :style="
                      Object.keys(groups.rectangles).length === 0
                        ? {}
                        : {
                            backgroundColor: 'white',
                            color: 'blue',
                          }
                    "
                    :disabled="Object.keys(groups.rectangles).length === 0"
                    @click="handleSelectAll"
                  >
                    <template #icon>
                      <SelectOutlined />
                    </template>
                  </a-button>
                </a-tooltip>
              </a-popover>

              <a-tooltip v-if="copiedShapes != null" placement="topLeft">
                <template #title> Paste (Ctrl + V) </template>
                <a-button
                  id="zoom-in-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :style="{
                    backgroundColor: 'white',
                    color: 'blue',
                  }"
                  @click="pasteShapes"
                >
                  <template #icon>
                    <SnippetsOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip placement="topLeft">
                <template #title>Fit Window (Ctrl + F)</template>
                <a-button
                  id="zoom-in-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :style="
                    layerScale <= 1
                      ? {}
                      : {
                          backgroundColor: 'white',
                          color: 'blue',
                        }
                  "
                  :disabled="layerScale <= 1"
                  @click="handleLayerFitWindow"
                >
                  <template #icon>
                    <FullscreenOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip placement="topLeft">
                <template #title> Segmentation </template>
                <a-button
                  id="segment-btn"
                  shape="circle"
                  type="primary"
                  :size="'large'"
                  :style="{
                    backgroundColor: enableSegmentation ? 'blue' : 'white',
                    color: enableSegmentation ? 'white' : 'blue',
                  }"
                  :disabled="imageLoading || isObjectFetching"
                  @click="handleSegmentation"
                >
                  <template #icon>
                    <HighlightOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip v-if="shouldPreserveSegmentation">
                <template #title>Preserve Segmentation</template>
                <a-button
                  id="preserve-segmentation-btn"
                  type="primary"
                  shape="circle"
                  :size="'large'"
                  @click="openSegmentModal = true"
                >
                  <template #icon>
                    <LockOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip>
                <template #title>Info</template>
                <a-button
                  id="mark-info-btn"
                  type="primary"
                  shape="circle"
                  :size="'large'"
                  :style="{
                    backgroundColor: 'white',
                    color: 'blue',
                  }"
                  @click="handleShortcutInfo"
                >
                  <template #icon>
                    <InfoCircleOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip>
                <template #title>Save (Ctrl + S)</template>
                <a-button
                  id="mark-object-save-btn"
                  type="primary"
                  shape="circle"
                  :size="'large'"
                  :disabled="!isAnythingUpdated || imageLoading"
                  @click="saveCurrentImageAnnotation"
                >
                  <template #icon>
                    <SaveOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip>
                <template #title>Save Empty Annotation (Ctrl + B)</template>
                <a-button
                  id="mark-object-save-btn"
                  type="primary"
                  shape="circle"
                  :size="'large'"
                  :disabled="disableSaveEmptyAnnotation || imageLoading"
                  @click="saveEmptyAnnotation"
                >
                  <template #icon>
                    <FileOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip>
                <template #title>Previous Image (A)</template>
                <a-button
                  id="mark-object-previous-btn"
                  type="primary"
                  shape="circle"
                  :size="'large'"
                  :disabled="prevImageButtonDisabled || imageLoading"
                  @click="changeImage(-1)"
                >
                  <template #icon>
                    <ArrowLeftOutlined />
                  </template>
                </a-button>
              </a-tooltip>

              <a-tooltip>
                <template #title>Next Image (D)</template>
                <a-button
                  id="mark-object-next-btn"
                  type="primary"
                  shape="circle"
                  :size="'large'"
                  :disabled="nextImageButtonDisabled || imageLoading"
                  @click="changeImage(1)"
                >
                  <template #icon>
                    <ArrowRightOutlined />
                  </template>
                </a-button>
              </a-tooltip>
            </a-space>
          </div>
        </template>

        <template v-if="!imageLoading" #cover>
          <div
            id="deleteContextMenu"
            :style="{
              display: deleteShow ? 'initial' : 'none !important',
            }"
          >
            <button id="delete-button" @click="deleteObject">Delete</button>
          </div>
          <v-stage
            ref="stage"
            key="stage"
            id="mo-define-stage"
            :config="stageSize"
            :style="layerStyle"
            @click="handleStageClick"
            @mousedown="handleStageMouseDown"
            @touchstart="handleStageMouseDown"
            @contextmenu="handleDeleteMenuShow"
            style="position: relative"
          >
            <v-layer
              ref="layer"
              :scaleX="layerScale"
              :scaleY="layerScale"
              :draggable="layerScale <= 1 || isDrawing ? false : true"
              @mousedown="handleLayerMouseDown"
              @mouseup="handleLayerMouseUp"
              @wheel="handleWheel"
              @mousemove="handleLayerMouseMove"
              @mouseout="handleMouseOutSegmentation"
              @click="handleLayerClick"
              @mouseleave="() => (mouseLeaveLayer = true)"
              @mouseenter="() => (mouseLeaveLayer = false)"
              :dragBoundFunc="layerDragBoundFunc"
            >
              <v-image ref="image" :config="imageConfig" />
              <v-image
                v-if="maskImage"
                ref="maskImage"
                :image="maskImage"
                :opacity="0.5"
                :config="{
                  ...stageSize,
                }"
              />
              <v-image
                v-if="hoverMaskImage"
                ref="hoverMaskImage"
                :image="hoverMaskImage"
                :opacity="0.3"
                :config="{
                  ...stageSize,
                }"
              />
              <v-circle
                v-for="circle in renderClickCircles"
                :key="circle.id"
                :x="circle.x"
                :y="circle.y"
                :radius="circle.radius"
                :fill="circle.fill"
              />
              <v-group
                v-for="(rect, index) in groups.rectangles"
                :key="rect.name + 1"
                :config="{ draggable: !isDrawing, id: `${rect.name + 1}` }"
                @dragstart="handleGroupDragStart"
                @dragmove="onGroupDragMove"
                @dragend="handleGroupDragEnd"
              >
                <template
                  v-if="
                    rect.is_object_visible !== undefined
                      ? rect.is_object_visible
                      : true
                  "
                >
                  <v-rect
                    :key="rect.name"
                    :config="{
                      ...rect,
                      x:
                        rect.x !== -1
                          ? rect.x
                          : Math.min(
                              rect.startPointX,
                              rect.startPointX + rect.width
                            ),
                      y:
                        rect.y !== -1
                          ? rect.y
                          : Math.min(
                              rect.startPointY,
                              rect.startPointY + rect.height
                            ),
                      width: Math.abs(rect.width),
                      height: Math.abs(rect.height),
                    }"
                    :ref="rect.name + '_ref'"
                    @click="
                      (event) => handleTransformDisplay(event, rect, index)
                    "
                    @dblclick="(event) => handleOpenFloatingBoat(event, rect)"
                    @transformend="handleTransformEnd"
                  />
                  <v-text
                    v-if="
                      rect.is_label_visible !== undefined
                        ? rect.is_label_visible
                        : true
                    "
                    :key="index"
                    :name="groups.labels[index].text"
                    :config="groups.labels[index]"
                  />
                </template>
              </v-group>
              <v-transformer
                v-if="!newRect"
                ref="transformer"
                :config="{
                  ...transformerConfig,
                  boundBoxFunc: setTransformerBound,
                }"
              />
            </v-layer>
          </v-stage>
        </template>

        <template #extra> </template>
      </a-card>
    </a-col>

    <a-col span="6" class="h-100 d-flex flex-column px-0 py-0">
      <ObjectSettings
        :image-obj="imageObj"
        :image-loading="imageLoading"
        :text-color="textColor"
        :model-choice="modelChoice"
        :object-list="annotateObjects"
        @updateChecked="updateChecked"
        @changeLabelColor="changeLabelColor"
        @changeLabelSize="changeLabelSize"
        @updateCurrentObject="updateCurrentObjectName"
        @remove-events="removeEvents"
        @subscribe-events="subscribeEvents"
      />
      <a-list
        id="mark-objects-annotate-obj-list"
        bordered
        class="objects-list"
        size="small"
        :loading="{ spinning: isObjectFetching, size: 'large' }"
        :data-source="labeledObjectList"
        item-layout="horizontal"
      >
        <template #header>
          <span>Objects</span>
        </template>
        <template #renderItem="{ item, index }">
          <a-list-item
            :id="'mo-annotate-obj-' + index"
            class="pl-2 clickable"
            :class="{
              'selected-object': selectedShapeName === item.name,
            }"
          >
            <a-list-item-meta @click.prevent="onObjectSelect(item)">
              <template #title>
                <span v-if="isAiAnnotate(item)">
                  <a-select
                    v-model:value="aiAnnotationItem[item.name]"
                    class="w-100"
                    :options="getAiAnnotationOptions(item)"
                    @change="handleChangeAiAnnotationObject(item)"
                  />
                </span>
                <span v-else>
                  {{ item.actual_name }}
                </span>
              </template>
            </a-list-item-meta>
            <template #actions>
              <a-button
                v-if="isAiAnnotate(item)"
                type="primary"
                @click="handleAiAnnotateItemChange(item)"
              >
                <template #icon>
                  <check-outlined />
                </template>
              </a-button>
              <a-tag v-else-if="item?.segment_propagation" color="blue">
                AI Segment Propagation
              </a-tag>
            </template>
          </a-list-item>
        </template>
      </a-list>
      <file-list
        :image-list="imageList"
        :index="imageIndexList"
        @loadImage="loadImageRange"
        @remove-events="removeEvents"
        @subscribe-events="subscribeEvents"
      />
    </a-col>
  </a-row>
</template>

<script>
import { isHandAndFaceObj } from '@/utils/detector';
import { deepClone } from '@/utils/task';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BorderOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  FullscreenOutlined,
  HighlightOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  LockOutlined,
  SaveOutlined,
  SelectOutlined,
  SnippetsOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons-vue';
import axios from 'axios';
import Loading from 'src/components/core/Loading.vue';
import OperationProgress from 'src/components/shared/Components/OperationProgress.vue';
import { getRandomColor } from 'src/config/color-config';
import objectSegmentationMixin from 'src/mixins/objectSegmentation';
import objectsMarkingMixin from 'src/mixins/objectsMarking';
import DetectorService from 'src/services/detector.js';
import LRTService from 'src/services/longRunningTask';
import S3Service from 'src/services/s3';
import SQSServices from 'src/services/sqs';
import store from 'src/store/index';
import { uuid } from 'vue-uuid';
import { mapActions, mapGetters } from 'vuex';
import httpClient from '../../../../../service/httpClient';
import {
  createNewRectangleConfig,
  createNewTextConfig,
  parseStringCoordsToObj,
  scaleRectangleDimension,
} from '../../LabelData/ObjectAnnotation/helpers';
import * as OBJECT from '../../LabelData/ObjectAnnotation/object';
import {
  isDefaultObjects,
  longRunningTaskStatus,
  ODQueues,
  shortcutKeys,
  taskObjectListColumns,
  TransformerConfig,
} from './config';
import FileList from './FileList.vue';
import ObjectSettings from './ObjectSettings.vue';
import ShotcutKeysModal from './ShortcutKeysModal.vue';
export default {
  name: 'ObjectsLabellingTool',
  components: {
    LockOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    BorderOutlined,
    HistoryOutlined,
    HighlightOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    SelectOutlined,
    CopyOutlined,
    SnippetsOutlined,
    FullscreenOutlined,
    SaveOutlined,
    FileOutlined,
    InfoCircleOutlined,
    CheckOutlined,
    FileList,
    ObjectSettings,
    ShotcutKeysModal,
    Loading,
    OperationProgress,
  },

  mixins: [objectsMarkingMixin, objectSegmentationMixin],
  inject: ['toast'],

  props: [
    'imagePathInBucket',
    'imageList',
    'imageId',
    'imageObj',
    'imageIndex',
    'totalResults',
    'currentPage',
    'pageSize',
    'startIndex',
    'endIndex',
    'presignedUrlList',
    'annotationFilterValues',
    'sortingValue',
    'reloadPresignedUrl',
    'model',
    'maskData',
    'enableFastAnnotation',
  ],

  emits: [
    'changeImageAnnotationPath',
    'nextImage',
    'prevImage',
    'closeModal',
    'updateAnnotationPath',
    'updateCacheIndex',
    'preserveMaskDataInput',
    'setFastAnnotation',
  ],

  setup() {
    return {
      isHandAndFaceObj,
      isDefaultObjects,
      taskObjectListColumns,
    };
  },

  data() {
    return {
      newRect: null,
      isNewRectAdded: false,
      isAnnotateObjectChecked: true,
      creatingTask: false,
      selectedShape: null,
      selectedShapes: [],
      showSaveButton: false,
      newLabel: '',
      annotate: null,
      debounceTimeout: null,
      generatingImageEmbedding: null,
      labeledObjectList: [],
      disableDelete: false,
      selectedShapeName: '',
      selectedShapeId: -1,
      imageToBeAnnotated: '',
      loadingSemtrack: false,
      image: null,
      imageLoading: true,
      groups: {
        rectangles: {},
        labels: {},
      },
      imageDimensions: {
        width: 0,
        height: 0,
      },
      stageSize: {
        width: 640,
        height: 480,
      },
      transformerConfig: TransformerConfig,
      currentObjectName: null,
      currentObjectId: -1,
      isAddingRectangle: false,
      isHistoryApplied: false,
      textColor: 'white',
      currentLabelSize: 15,
      labelVerticalGap: 17,
      img_idx: -1,
      isObjectFetching: false,
      isAnythingUpdated: false,
      debouncedChangeImage: null,
      temp_groups: {
        rectangles: {},
        labels: {},
      },
      deleteShow: false,
      layerScale: 1,
      scaleBy: 1.05,
      isWheelScroll: false,
      isLayerDragging: false,
      annotationCache: {},
      embeddingsCache: {},
      imagePresignedUrl: {},
      currentRectName: null,
      isDrawing: false,
      imageIndexList: this.imageIndex,
      isCachingImage: false,
      isCachingAnnotation: false,
      activeObjectArea: false,
      objectName: '',
      shortcutModal: false,
      keyHandlers: shortcutKeys,
      initialGroupPosition: { x: 0, y: 0 },
      initialRectPositions: [],
      isAllNodeSelected: false,
      uploadQueue: Promise.resolve(),
      annotateObjectStroke: null,
      searchValue: null,
      copiedShapes: null,
      isApplyPreviousLabel: false,
      darkRedColor: '#ff0000',
      defaultLabelSize: 15,
      aiAnnotationItem: {},
      displayReviewInfoMessage: false,
      mouseLeaveLayer: false,
      enableSegmentation: false,
      isSegmentationHover: false,
      hoverMaskImage: null,
      maskImage: null,
      tensorEmbedding: {
        embedding: null,
        high_res_0: null,
        high_res_1: null,
      },
      clicks: [],
      maskList: [],
      previousMaskData: [],
      openSegmentModal: false,
      selectedRow: null,
      shouldPreserveSegmentation: false,
      modelSizeOffset: 1024,
      longRunningTaskId: null,
      interval: null,
      showLoading: false,
      embeddingProgress: [],
      isGeneratingMask: false,
    };
  },

  computed: {
    ...mapGetters([
      'organization',
      'taskObjectList',
      'taskObjects',
      'selectedTask',
      'prevGroups',
      'prevPolygons',
      'modelChoice',
      'imagesForAnnotation',
      'prevPageLastImageObj',
      'taskParameters',
      'taskName',
    ]),

    rowSelection() {
      return {
        type: 'radio',
        selectedRowKeys: [this.selectedRow],
        onChange: this.onRowSelect,
      };
    },

    renderClickCircles() {
      return this.maskList.map((click, index) => {
        const color = click.clickType === 0 ? 'red' : 'darkblue';
        return {
          x: click.x * this.stageSize.width,
          y: click.y * this.stageSize.height,
          radius: 5,
          fill: color,
          id: `click-circle-${index}`,
        };
      });
    },

    isPopoverVisible() {
      return this.selectedShapeName || this.isAllNodeSelected;
    },

    checkEmptyObject() {
      if (
        (Array.isArray(this.annotationCache[this.imageIndex]) &&
          this.annotationCache[this.imageIndex].length === 0) ||
        (this.annotationCache[this.imageIndex] &&
          Object.keys(this.annotationCache[this.imageIndex]).length === 0)
      ) {
        return true;
      } else {
        return false;
      }
    },
    disableSaveEmptyAnnotation() {
      if (this.checkEmptyObject) {
        return true;
      }
      return false;
    },
    annotationObjectList() {
      if (!this.annotateObjects || this.annotateObjects.length === 0) {
        return [];
      }
      return this.annotateObjects.map((o) => ({
        value: o.id,
        label: o.name,
      }));
    },

    layerStyle() {
      return {
        cursor:
          this.isAddingRectangle || this.isDrawing
            ? 'crosshair'
            : this.isLayerDragging
            ? 'grab'
            : 'default',
      };
    },

    nextImageButtonDisabled() {
      return this.imageIndex + 1 >= this.totalResults;
    },

    prevImageButtonDisabled() {
      return this.imageIndex - 1 < 0;
    },

    annotateObjects() {
      return this.taskObjects
        .filter((obj) => !obj.is_static && !isDefaultObjects(obj.name))
        .sort();
    },

    nonStaticObjects() {
      return this.taskObjects
        .filter((obj) => obj && !obj.is_static && !isDefaultObjects(obj.name))
        .sort()
        .map((o, index) => {
          return { ...o };
        });
    },

    imageConfig() {
      return {
        image: this.image,
        name: 'currentImage',
        ...this.stageSize,
      };
    },

    objectStrokeColor() {
      let stroke = {};
      if (this.annotateObjects.length === 0) return stroke;
      this.annotateObjects.forEach((item) => {
        if (isHandAndFaceObj(item.name, this.modelChoice)) {
          stroke[item.name] = this.darkRedColor;
        } else stroke[item.name] = item.outline_color;
      });
      return stroke;
    },

    isLayerAtDefaultScale() {
      return this.layerScale <= 1;
    },
  },

  watch: {
    layerScale(value) {
      if (this.isAllNodeSelected) this.handleDeselectAll();
      if (!this.isWheelScroll) this.updateLayerZoom(value);
      if (value === 1) {
        const layer = this.$refs.layer.getNode();
        layer.x(0);
        layer.y(0);
        this.isLayerDragging = false;
      }
    },

    async imageObj(value) {
      if (!value) return;
      this.imageLoading = true;
      const res = await this.loadImage();
      if (!res) {
        this.imageLoading = false;
        return;
      }
      await this.renderExistingObjectList();
      this.imageLoading = false;
    },

    textColor(newColor) {
      const { labels } = this.groups;
      const newLabels = Object.entries(labels).reduce((res, [key, value]) => {
        res[key] = { ...value, color: newColor, fill: newColor };
        return res;
      }, {});
      this.groups['labels'] = newLabels;
    },

    async reloadPresignedUrl(value) {
      if (!value) return;
      await this.preloadImageAndAnnotation();
      this.cacheEmbeddings();
    },

    showLoading(value) {
      if (value) {
        document.removeEventListener('keydown', this.handleKeyDownEvents);
        document.removeEventListener('mouseup', this.handleDocumentMouseUp);
      } else {
        document.addEventListener('keydown', this.handleKeyDownEvents);
        document.addEventListener('mouseup', this.handleDocumentMouseUp);
      }
    },

    enableSegmentation(value) {
      this.isAddingRectangle = false;
      if (value) this.$emit('setFastAnnotation', false);
    },
  },

  unmounted() {
    try {
      this.resetInitState();
      this.removeEvents();
      clearInterval(this.interval);
    } catch (err) {
      console.error(err);
    }
  },

  async created() {
    await this.preloadImageAndAnnotation();
    this.isObjectFetching = true;
    this.imageLoading = true;
    const res = await this.loadImage();
    if (!res) {
      this.imageLoading = false;
      this.isObjectFetching = false;
      return;
    }
    this.imageLoading = false;
    this.img_idx = this.imageIndex;
    this.imageIndexList = this.img_idx;
    await this.updateImageConfig();
    await this.renderExistingObjectList();
    await this.updateStats();
    this.isObjectFetching = false;
    if (this.img_idx > 0) {
      if (
        this.annotationCache[this.img_idx - 1] == null ||
        this.annotationCache[this.img_idx - 1] === ''
      ) {
        this.isApplyPreviousLabel = true;
      }
    }
    this.interval = setInterval(this.getEmbeddingProgress, 5000);
    this.cacheEmbeddings();
  },

  mounted() {
    this.subscribeEvents();
  },

  methods: {
    ...mapActions([
      'addTaskObject',
      'setTaskObjectList',
      'setPrevGroups',
      'setPrevPolygons',
      'getTaskObjects',
      'updateTaskObject',
    ]),

    async cacheCurrentImageEmbedding() {
      if (!this.enableFastAnnotation) return;
      if (!this.presignedUrlList.embeddings[this.imageIndex]) return;
      if (this.embeddingsCache[this.imageIndex]) return;
      this.toast.info('Please wait while we get image embeddings');
      this.embeddingsCache[this.imageIndex] = await this.getAnnotationFromUrl(
        this.presignedUrlList.embeddings[this.imageIndex],
        true,
        true
      );
    },

    closePreserveSegmentationModal() {
      this.selectedRow = null;
      this.openSegmentModal = false;
    },

    onRowSelect(selectedRowKeys) {
      this.selectedRow = selectedRowKeys[0];
    },

    async getAnnotationEmbedding() {
      try {
        if (this.embeddingsCache[this.imageIndex]) {
          return this.embeddingsCache[this.imageIndex];
        }

        this.toast.info('Getting embeddings. Please wait!');
        let embeddingPath = this.imageObj.path_to_embeddings;
        if (!embeddingPath) embeddingPath = this.getEmbeddingPath();

        const [error, data] = await S3Service.generatePresignedUrl(
          `${this.organization}-training`,
          embeddingPath,
          '',
          false
        );

        if (error) {
          this.toast.error('No embedding found.');
          return null;
        }
        const response = await axios.get(data.presigned_url, {
          responseType: 'arraybuffer',
        });
        return response.data;
      } catch (err) {
        console.error(err);
        return null;
      }
    },

    getEmbeddingPath() {
      return (
        this.imageObj.path_to_image.replace('images', 'embeddings') + '.npz'
      );
    },

    setEmbeddingProgress(item) {
      this.embeddingProgress = [];
      let payload = {
        id: item.id,
        name: 'Image Embedding Process',
        status:
          item.status === longRunningTaskStatus.STARTED
            ? longRunningTaskStatus.STARTED
            : 'Queue',
        progress: item.details.progress,
      };
      if (item.details.progress > 0) payload.progress = item.details.progress;
      this.embeddingProgress.push(payload);
    },

    async getEmbeddingProgress() {
      if (!this.longRunningTaskId) return;
      const [error, data] = await LRTService.getLongRunningTaskById(
        this.longRunningTaskId,
        false
      );
      if (error) {
        this.longRunningTaskId = null;
        this.embeddingProgress = [];
      }
      this.setEmbeddingProgress(data);
      if (
        data.status === longRunningTaskStatus.FINISHED ||
        data.status === longRunningTaskStatus.FAILED
      ) {
        this.longRunningTaskId = null;
        this.embeddingProgress = [];
      }
      if (data.status === longRunningTaskStatus.FINISHED) {
        await this.getEmbedddingAndLoadTensors();
        if (!this.imageObj.path_to_embeddings)
          this.$emit('updateAnnotationPath', {
            path_to_embeddings: this.getEmbeddingPath(),
          });
        this.showLoading = false;
      } else if (data.status === longRunningTaskStatus.FAILED) {
        this.toast.error('Failed generate embedding.');
        this.showLoading = false;
      }
    },

    async getLongRunningTaskId() {
      const payload = {
        name: 'generate_image_embedding',
        status: longRunningTaskStatus.PENDING,
        details: {
          progress: 0,
        },
        event: {
          annotation_id: this.imageObj.id,
        },
        organization: this.organization,
        task: this.selectedTask,
      };

      const [error, data] = await LRTService.createLongRunningTasks(payload);
      if (error) return null;
      return data.id;
    },

    async generateEmbedding() {
      this.showLoading = true;
      this.toast.info('Generating image embedding. Please wait!');
      this.longRunningTaskId = await this.getLongRunningTaskId();
      if (!this.longRunningTaskId) {
        this.showLoading = false;
        return this.toast.error('Failed to generate embedding!');
      }
      const payload = {
        queueName: ODQueues.AcpCpuServices,
        sqsMessage: {
          long_running_task_id: this.longRunningTaskId,
          path_to_image: this.imageObj.path_to_image,
          task_name: this.taskName,
          annotation_id: this.imageObj.id,
          organization: this.organization,
          message_type: 'image_embedding',
        },
      };

      const [error] = await SQSServices.sendSQSMessage(payload, false);
      if (error) {
        this.showLoading = false;
        return this.toast.error('Failed to generate embedding.');
      }
    },

    preserveSegmentationState() {
      if (!this.selectedRow) return this.toast.info('Please select object.');
      this.setItemInLocalStorage(this.maskList);
      this.openSegmentModal = false;
      this.selectedRow = null;
      this.shouldPreserveSegmentation = false;
      this.toast.success('Segmentation state preserved successfully.');
    },

    setItemInLocalStorage(item) {
      let segmentPropagation = {};
      const taskObject = this.getAnnotationLabelObject(this.selectedRow);
      const imageNaturalWidth = this.imageConfig.image.width;
      const imageNaturalHeight = this.imageConfig.image.height;
      segmentPropagation['labels_coordinates'] = {};
      segmentPropagation['labels_coordinates'][taskObject.name] = {};
      segmentPropagation['labels_coordinates'][taskObject.name]['points'] =
        item.map((p) => [p.x * imageNaturalWidth, p.y * imageNaturalHeight]);
      segmentPropagation['labels_coordinates'][taskObject.name]['labels'] =
        item.map((p) => p.clickType);
      segmentPropagation['labels_coordinates'][taskObject.name]['id'] =
        taskObject.id;
      segmentPropagation['annotationId'] = this.imageId;
      segmentPropagation['selectedTask'] = this.selectedTask;
      localStorage.setItem(
        'segmentPropagation',
        JSON.stringify(segmentPropagation)
      );

      this.$emit('preserveMaskDataInput', {
        selectedAnnotation: this.imageObj.id,
        mask: this.previousMaskData,
      });
    },

    getMaskClicksAndPreviousMask() {
      if (
        !this.maskData.selectedAnnotation ||
        this.maskData.selectedAnnotation !== this.imageObj.id
      )
        return;
      const item = JSON.parse(localStorage.getItem('segmentPropagation'));
      if (!item || item?.selectedTask !== this.selectedTask) return;

      const imageWidth = this.imageConfig?.image?.width;
      const imageHeight = this.imageConfig?.image?.height;
      if (!imageWidth || !imageHeight || !item.labels_coordinates) return;
      this.previousMaskData = this.maskData.mask;
      this.maskList = [];
      this.clicks = [];
      for (const [labelKey, labelData] of Object.entries(
        item.labels_coordinates
      )) {
        const { points, labels } = labelData;

        for (let i = 0; i < points.length; i++) {
          const [xRaw, yRaw] = points[i];
          const clickType = labels[i];

          const x = xRaw / imageWidth;
          const y = yRaw / imageHeight;

          this.maskList.push({ x, y, clickType });
          this.clicks.push({
            x: x * this.modelSizeOffset,
            y: y * this.modelSizeOffset,
            clickType,
          });
        }
      }
    },

    async startFastAnnotation() {
      this.clearCurrentMask();
      await this.getEmbeddingAndStartModel();
    },

    async getEmbeddingAndStartModel() {
      if (this.tensorEmbedding.embedding !== null) return;
      this.showLoading = true;
      if (
        !this.imageObj.path_to_embeddings &&
        !this.embeddingsCache[this.imageIndex]
      )
        return await this.generateEmbedding();
      await this.getEmbedddingAndLoadTensors();
      this.showLoading = false;
    },

    async getEmbedddingAndLoadTensors() {
      const embeddingsData = await this.getAnnotationEmbedding();
      if (!embeddingsData) return;
      if (this.enableFastAnnotation && !this.embeddingsCache[this.imageIndex])
        this.embeddingsCache[this.imageIndex] = embeddingsData;
      await this.loadEmbeddingTensors(embeddingsData);
    },

    async handleSegmentation() {
      this.enableSegmentation = !this.enableSegmentation;
      if (this.enableSegmentation && this.tensorEmbedding.embedding == null) {
        if (!this.imageObj.path_to_embeddings)
          return await this.generateEmbedding();
        else {
          this.showLoading = true;
          await this.getEmbedddingAndLoadTensors();
          this.showLoading = false;
        }
      }

      this.hoverMaskImage = null;
      if (!this.maskList.length) {
        this.clicks = [];
        this.maskImage = null;
        this.previousMaskData = [];
      }
    },

    updateTransformerRotationHeight() {
      if (
        !this.selectedShapeName ||
        !this.taskParameters?.telemetryParams?.object_rotation
      )
        return;
      try {
        const stage = this.$refs.stage.getStage();
        const layer = this.$refs.layer.getNode();
        const transformerNode = this.$refs.transformer.getNode();
        const selectedNode = stage.findOne('.' + this.selectedShapeName);
        if (selectedNode === null || selectedNode === undefined) return;
        const height = selectedNode?.height();
        if (height === null || height === undefined) return;
        transformerNode.rotateAnchorOffset(-Math.floor(height / 4));

        if (this.taskParameters?.telemetryParams?.object_rotation)
          transformerNode.rotateEnabled(true);
        layer.batchDraw();
      } catch (error) {
        console.error(error);
      }
    },

    getAnnotationLabelObject(objectId) {
      return this.annotateObjects.find((obj) => obj.id == objectId);
    },

    setTransformerBound(oldBox, newBox) {
      const layer = this.$refs.layer.getNode();
      const layerWidth = layer.width();
      const layerHeight = layer.height();

      const adjustedCorners = this.getRotatedRectangleVertices(
        newBox.x,
        newBox.y,
        newBox.rotation,
        newBox.width,
        newBox.height
      );

      const isWithinBounds = Object.values(adjustedCorners).every((corner) => {
        return (
          corner.x > 0 &&
          corner.x < layerWidth &&
          corner.y > 0 &&
          corner.y < layerHeight
        );
      });

      return isWithinBounds ? newBox : oldBox;
    },

    handleSearchChange(value) {
      if (!value) return;
      this.searchValue = value;
      this.annotate = this.searchValue;
    },

    ResizeStageObjects(e) {
      this.$nextTick(() => {
        this.resizeAnnotationCanvasObjectDimensions();
        this.updateLabelSize();
        if (this.newRect && this.activeObjectArea && !this.creatingTask) {
          this.removeRuntimeObject();
          this.isNewRectAdded = false;
          this.currentRectName = null;
        }
        if (!this.isAnythingUpdated) this.createGroupClone();
      });
    },

    updateStageAndImageSize(cover) {
      this.stageSize.width = cover.offsetWidth;
      this.stageSize.height = cover.offsetHeight;
      this.imageDimensions.width = this.stageSize.width;
      this.imageDimensions.height = this.stageSize.height;
    },

    normalizeRectangle(rectangles) {
      for (const [name, rectangle] of Object.entries(rectangles)) {
        this.normalizeDim(
          rectangle,
          this.stageSize.width,
          this.stageSize.height
        );
      }
    },

    deNormalizeRectangle(rectangles, labels) {
      for (const [name, rectangle] of Object.entries(rectangles)) {
        scaleRectangleDimension(
          rectangle,
          this.stageSize.width,
          this.stageSize.height
        );
        const yAxis = this.getDefaultLabelCoordinate(rectangle.y);
        const xAxis = rectangle.x;
        labels[name].x = xAxis;
        labels[name].y = yAxis;
      }
    },

    async handleImageObj(value) {
      if (!value) return;
      this.imageLoading = true;
      const res = await this.loadImage();
      if (!res) {
        this.imageLoading = false;
        return;
      }
      await this.renderExistingObjectList();
      this.imageLoading = false;
    },

    handleOpenFloatingBoat(event, rect) {
      if (this.newRect !== null) {
        return;
      }
      if (event.evt?.button !== 0) return;
      this.handleFloatingBoatPosition(rect);
    },

    updateChecked(checked) {
      this.isAnnotateObjectChecked = checked;
    },

    async addObject(payload) {
      return await this.addTaskObject(payload);
    },

    isValidPattern(value) {
      const pattern = new RegExp(
        '^[A-Za-z](?:[A-Za-z0-9_-]*[A-Za-z0-9])?(?: [A-Za-z0-9_-]+)*$'
      );
      return pattern.test(value);
    },

    isNavigationObject(objectName) {
      const object = ('' + objectName).toLowerCase();
      return object === 'next' || object === 'previous';
    },

    async handleSave() {
      if (!this.annotate) return;
      if (typeof this.annotate == 'string') {
        if (!this.isValidPattern(this.annotate?.toLowerCase())) {
          return this.toast.info(
            'Object name should only contains valid Alpha numeric words!'
          );
        }
      }
      this.creatingTask = true;
      let currentObj = null;
      if (typeof this.annotate == 'string') {
        if (
          this.annotate === 'hand' ||
          this.annotate === 'face' ||
          this.isDefaultObjects(this.annotate)
        ) {
          this.creatingTask = false;
          return this.toast.error(
            `Creating an annotation for "${this.annotate}" is not permitted.`
          );
        }
        currentObj = this.taskObjects.find(
          (obj) => obj.name?.toLowerCase() === this.annotate?.toLowerCase()
        );
      }
      if (typeof this.annotate == 'string' && !currentObj) {
        return await this.createObject(currentObj);
      }
      const objectToCheck =
        currentObj ?? this.taskObjects.find((data) => data.id == this.annotate);
      if (this.isDefaultObjects(objectToCheck.name)) {
        this.creatingTask = false;
        return this.toast.error(
          `Creating an annotation for "${objectToCheck.name}" is not permitted.`
        );
      }
      if (isHandAndFaceObj(objectToCheck.name, this.modelChoice)) {
        objectToCheck.outline_color = this.darkRedColor;
      }
      const { outline_color, name, id } = objectToCheck;

      this.groups.rectangles = {
        ...this.groups.rectangles,
        [this.selectedShapeId]: {
          ...this.groups.rectangles[this.selectedShapeId],
          actual_name: name,
          stroke: outline_color,
          actual_id: id,
        },
      };
      this.groups.labels = {
        ...this.groups.labels,
        [this.selectedShapeId]: {
          ...this.groups.labels[this.selectedShapeId],
          text: name,
        },
      };
      this.addObjectInLabeledList(this.groups.rectangles[this.selectedShapeId]);
      this.resetRuntimeObject();
      // this.cacheonSelect();
    },

    resetRuntimeObject() {
      this.isAnythingUpdated = true;
      this.annotate = null;
      this.searchValue = null;
      this.newRect = null;
      this.creatingTask = false;
      this.activeObjectArea = false;
      this.annotateObjectStroke = null;
    },

    async createObject(currentObj) {
      const payload = {
        name: this.annotate,
        task: this.selectedTask,
        outline_color: this.groups.rectangles[this.selectedShapeId].stroke,
      };
      await this.addObject(payload);
      await this.getTaskObjects(this.selectedTask);
      currentObj = this.taskObjects.find((obj) => obj.name === this.annotate);
      this.groups.rectangles[this.selectedShapeId].actual_id = currentObj.id;
      this.groups.rectangles = {
        ...this.groups.rectangles,
        [this.selectedShapeId]: {
          ...this.groups.rectangles[this.selectedShapeId],
          actual_name: this.annotate,
          stroke: payload.outline_color,
        },
      };
      this.groups.labels = {
        ...this.groups.labels,
        [this.selectedShapeId]: {
          ...this.groups.labels[this.selectedShapeId],
          text: this.annotate,
        },
      };
      this.addObjectInLabeledList(this.groups.rectangles[this.selectedShapeId]);
      this.resetRuntimeObject();
    },

    onSearch(value) {
      this.objectName = value;
    },

    filterOption(input, option) {
      if (option.label.toLowerCase().startsWith(input.toLowerCase())) {
        return option.label.toLowerCase().startsWith(input.toLowerCase());
      } else {
        this.annotate = input;
        return;
      }
    },

    handleShortcutInfo() {
      this.displayShortcutModal(true);
    },

    displayShortcutModal(visible) {
      this.shortcutModal = visible;
    },

    removeEvents() {
      document.removeEventListener('keydown', this.handleKeyDownEvents);
      document.removeEventListener('mouseup', this.handleDocumentMouseUp);
      window.removeEventListener('resize', this.ResizeStageObjects);
    },

    subscribeEvents() {
      document.addEventListener('keydown', this.handleKeyDownEvents);
      document.addEventListener('mouseup', this.handleDocumentMouseUp);
      window.addEventListener('resize', this.ResizeStageObjects);
    },

    handleDocumentMouseUp() {
      if (!this.mouseLeaveLayer) return;
      if (!this.isDrawingRectangle()) return;
      let position = this.getCurrentRectangleFromLayer() || {};
      this.updateRectPosition(position);
      this.resetObjectDrawing();
      this.isLayerDragging = false;
      this.isAnythingUpdated = true;
      this.showLabelPanel();
    },

    handlePreviousImage() {
      if (this.prevImageButtonDisabled || this.imageLoading) return;
      this.changeImage(-1);
    },

    handleNextImage() {
      if (this.nextImageButtonDisabled || this.imageLoading) return;
      this.changeImage(1);
    },

    handleTransformDisplay(event, shape, index) {
      // this.isAddingRectangle = false;
      if (this.newRect || this.isAddingRectangle) {
        return;
      }
      if (event.evt.ctrlKey) {
        const transformerNode = this.$refs.transformer.getNode();
        const stage = transformerNode.getStage();
        const layer = this.$refs.layer.getNode();
        const selectedNode = stage.findOne('.' + shape.name);
        if (selectedNode && !this.selectedShapes.includes(selectedNode)) {
          this.selectedShapes.push(selectedNode);

          // Add a new transformer to the selected node
          const newTransformer = new Konva.Transformer({
            node: selectedNode,
            name: 'transformer',
            ...this.transformerConfig,
          });
          layer.add(newTransformer);
        }
      } else {
        this.selectedShapes = [];
      }
      this.selectedShapeName = shape.name;
      this.selectedShape = shape;
      this.selectedShapeId = index;
    },

    handleTransformEnd(e) {
      this.isAnythingUpdated = true;
      this.handleTransform(e, true);
    },
    handleGroupDragStart(event) {
      if (!this.isAllNodeSelected) return;
      // Store the initial group position
      this.initialGroupPosition = { ...event.target.position() };

      // Store the initial positions of the selected rectangles
      const selectedShapeNames = Object.keys(this.groups.rectangles);
      for (const shapeName of selectedShapeNames) {
        this.initialRectPositions[shapeName] = {
          x: this.groups.rectangles[shapeName].x,
          y: this.groups.rectangles[shapeName].y,
          startx: this.groups.rectangles[shapeName].startPointX,
          starty: this.groups.rectangles[shapeName].startPointY,
          labelX: this.groups.labels[shapeName].x,
          labelY: this.groups.labels[shapeName].y,
        };
      }
    },

    restrictToBoundary(event) {
      const layer = this.$refs.layer.getNode();
      const group = event.target;

      const scale = layer.scaleX();
      const groupRect = group.getClientRect();

      let newX = group.x() / scale;
      let newY = group.y() / scale;

      if (groupRect.x < 0) {
        newX -= groupRect.x / scale;
      } else if (groupRect.x + groupRect.width > layer.width()) {
        newX -= (groupRect.x + groupRect.width - layer.width()) / scale;
      }

      if (groupRect.y < 0) {
        newY -= groupRect.y / scale;
      } else if (groupRect.y + groupRect.height > layer.height()) {
        newY -= (groupRect.y + groupRect.height - layer.height()) / scale;
      }

      group.position({ x: newX * scale, y: newY * scale });
    },

    onGroupDragMove(event) {
      if (!this.isAllNodeSelected) {
        if (this.isLayerAtDefaultScale) this.restrictToBoundary(event);
        return;
      }
      const newGroupPosition = event.target.position();
      const selectedShapeNames = Object.keys(this.groups.rectangles);
      const deltaX = newGroupPosition.x - this.initialGroupPosition.x;
      const deltaY = newGroupPosition.y - this.initialGroupPosition.y;
      selectedShapeNames.forEach((shapeName) => {
        if (this.selectedShapeName != shapeName) {
          const initialPosition = this.initialRectPositions[shapeName];
          const rectangle = this.groups.rectangles[shapeName];
          const label = this.groups.labels[shapeName];

          let newX = initialPosition.x + deltaX;
          let newY = initialPosition.labelY + deltaY;
          const groupHeight =
            rectangle.height +
            this.labelVerticalGap +
            (this.currentLabelSize - this.defaultLabelSize);
          const groupWidth = rectangle.width;
          if (newX < 0) {
            newX = 0;
          } else if (newX + groupWidth > this.stageSize.width) {
            newX = this.stageSize.width - groupWidth;
          }

          if (newY < 0) {
            newY = 0;
          } else if (newY + groupHeight > this.stageSize.height) {
            newY = this.stageSize.height - groupHeight;
          }

          rectangle.x = newX;
          label.x = newX;
          label.y = newY;
          newY += this.labelVerticalGap;
          rectangle.y = newY;
          if (this.currentLabelSize > this.defaultLabelSize) {
            const offset = this.currentLabelSize - this.defaultLabelSize;
            label.y -= offset;
            rectangle.y += offset;
          }
        }
      });

      this.restrictToBoundary(event);
      this.isAnythingUpdated = true;
    },

    resetTransform() {
      const transformerNode = this.$refs?.transformer?.getNode();
      if (!transformerNode) return;
      transformerNode?.nodes([]);
    },

    handleGroupDragEnd(event) {
      const group = event.target;
      const rect = group.children[0];
      const label = group.children[1];
      const name = rect.name();
      const position = this.restrictRectPositionInZoom(rect, label);
      this.updateGroupItemsCoordinates(name, position);
      this.isAnythingUpdated = true;
      group.position({ x: 0, y: 0 });
    },

    restrictRectPositionInZoom(rect, label) {
      const layer = this.$refs.layer.getNode();
      if (this.isLayerAtDefaultScale) return rect.getAbsolutePosition(layer);

      let position = {
        x: rect.getAbsolutePosition(layer).x,
        y: label.getAbsolutePosition(layer).y,
      };

      const groupHeight =
        rect.height() +
        this.labelVerticalGap +
        (this.currentLabelSize - this.defaultLabelSize);

      position.x = Math.max(
        0,
        Math.min(position.x, layer.width() - rect.width() - 1)
      );
      position.y = Math.max(
        0,
        Math.min(position.y, layer.height() - groupHeight - 1)
      );
      position.y += this.labelVerticalGap;

      if (this.currentLabelSize > this.defaultLabelSize)
        position.y =
          position.y + (this.currentLabelSize - this.defaultLabelSize);

      return position;
    },

    updateGroupItemsCoordinates(name, position) {
      const { x, y } = position;
      this.groups.rectangles[name].x = x;
      this.groups.rectangles[name].y = y;
      this.groups.labels[name].x = x;
      this.groups.labels[name].y =
        this.getLabelVerticalCoordinate(name) ??
        this.getDefaultLabelCoordinate(y);
      if (this.groups.labels[name].y < 0) {
        this.groups.labels[name].y += this.groups.rectangles[name].height;
      }
    },

    handleKeyDownEvents(e) {
      if (this.activeObjectArea && e.key == 'Enter') {
        this.handleSave();
        return;
      }
      if (this.activeObjectArea) {
        return;
      }
      for (const [
        key,
        { ctrl, alt, handler, insensitiveKey },
      ] of Object.entries(this.keyHandlers)) {
        let userKey = e.key;
        if (insensitiveKey && e.key?.toLowerCase() === key) {
          userKey = e.key?.toLowerCase();
        }
        if (
          userKey === key &&
          (ctrl === undefined || e.ctrlKey === ctrl) &&
          (alt === undefined || e.altKey === alt)
        ) {
          e.preventDefault();
          this[handler]();
          break;
        }
      }
    },

    layerDragBoundFunc(pos) {
      return this.updateLayerBounds(pos);
    },

    handleLayerZoomIn() {
      if (this.imageLoading) return;
      this.layerScale = this.layerScale * this.scaleBy;
    },

    handleLayerZoomOut() {
      if (this.isLayerAtDefaultScale) {
        this.resetLayerScale();
        return;
      }
      this.layerScale = this.layerScale / this.scaleBy;
    },

    handleSelectAll(e) {
      if (!this.isLayerAtDefaultScale)
        return this.toast.info('Please zoom out canvas to select all objects.');
      if (this.isAllNodeSelected) {
        this.handleDeselectAll();
        this.selectedShapeName = '';
        this.updateObjectTransformer();
        return;
      }
      const shape_name = Object.keys(this.groups.rectangles);
      const layer = this.$refs.layer.getNode();
      const transformerNode = this.$refs.transformer.getNode();
      const stage = transformerNode.getStage();
      const transformers = [];

      // Remove previous transformers if any
      layer
        .find('.transformer')
        .forEach((transformer) => transformer.destroy());

      for (const shape in shape_name) {
        const selectedNode = stage.findOne('.' + shape_name[shape]);
        if (selectedNode) {
          const newTransformer = new Konva.Transformer({
            node: selectedNode,
            name: 'transformer',
            ...this.transformerConfig,
          });
          layer.add(newTransformer);
          transformers.push(newTransformer);
        }
      }

      // layer.batchDraw();
      this.isAllNodeSelected = true;
    },

    handleDeselectAll() {
      try {
        const layer = this.$refs?.layer?.getNode();
        if (!layer) return;
        layer
          .find('.transformer')
          .forEach((transformer) => transformer.destroy());
        layer.batchDraw();
        this.isAllNodeSelected = false;
        this.selectedShapes = [];
      } catch (err) {
        console.error(err);
      }
    },
    copySelectedShapes() {
      const transformerNode = this.$refs.transformer.getNode();
      const stage = transformerNode.getStage();
      const selectedShapes = stage
        .find('.transformer')
        .map((transformer) => transformer.node());

      // If no transformers are found, check for single selected shape
      if (selectedShapes.length === 0 && this.selectedShapeName) {
        const selectedNode = stage.findOne('.' + this.selectedShapeName);
        if (selectedNode) {
          selectedShapes.push(selectedNode);
        }
      }
      this.copiedShapes = selectedShapes.map((shape) => {
        const shapeAttrs = { ...shape.attrs };

        return shapeAttrs;
      });
    },
    pasteShapes() {
      const newGroups = {
        rectangles: {},
        labels: {},
      };

      if (!this.copiedShapes || !this.copiedShapes.length) return;

      this.copiedShapes.forEach((copiedShape) => {
        const { width, height } = this.stageSize;

        // Create a new rectangle configuration by spreading `o` and adding additional properties
        let rectangle = {
          ...createNewRectangleConfig(copiedShape, width, height),
          id: uuid.v4(),
          width: copiedShape.width,
          height: copiedShape.height,
          x: copiedShape.x,
          y: copiedShape.y,
          actual_name: copiedShape.actual_name,
          stroke: copiedShape.stroke,
        };

        // Create a new label configuration
        const label = createNewTextConfig(
          rectangle.id,
          rectangle.actual_name,
          rectangle.x,
          rectangle.y - 15,
          this.textColor
        );

        // Assign rectangle and label to the newGroups
        newGroups.rectangles[rectangle.name] = rectangle;
        newGroups.labels[rectangle.name] = label;

        // Add the object to the labeled list
        this.addObjectInLabeledList(rectangle);
      });

      // Merge newGroups with existing groups
      this.groups.rectangles = {
        ...this.groups.rectangles,
        ...newGroups.rectangles,
      };
      this.groups.labels = { ...this.groups.labels, ...newGroups.labels };
      this.updateLabelSize();
      this.isAnythingUpdated = true;
    },
    moveShape(shapeName, deltaX = 0, deltaY = 0) {
      if (!shapeName || Number.isInteger(shapeName)) return;
      const rectangle = this.groups.rectangles[shapeName];
      const label = this.groups.labels[shapeName];
      const layer = this.$refs.layer.getNode();
      const layerWidth = layer.width();
      const layerHeight = layer.height();

      rectangle.x += deltaX;
      rectangle.y += deltaY;
      rectangle.startPointX += deltaX;
      rectangle.startPointY += deltaY;
      label.x += deltaX;
      label.y += deltaY;

      if (rectangle.x < 0) {
        this.revertXMovement(rectangle, label, deltaX);
      }

      if (rectangle.x + rectangle.width > layerWidth) {
        this.revertXMovement(rectangle, label, deltaX);
      }

      if (label.y < 0) {
        this.revertYMovement(rectangle, label, deltaY);
      }
      if (
        label.y +
          rectangle.height +
          this.labelVerticalGap +
          (this.currentLabelSize - this.defaultLabelSize) >
        layerHeight
      ) {
        this.revertYMovement(rectangle, label, deltaY);
      }
    },

    revertXMovement(rectangle, label, deltaX) {
      rectangle.x -= deltaX;
      rectangle.startPointX -= deltaX;
      label.x -= deltaX;
    },

    revertYMovement(rectangle, label, deltaY) {
      rectangle.y -= deltaY;
      rectangle.startPointY -= deltaY;
      label.y -= deltaY;
    },

    moveSelectedNodesup() {
      if (this.isAllNodeSelected) {
        const selectedShapeNames = Object.keys(this.groups.rectangles);
        selectedShapeNames.forEach((shapeName) => {
          this.moveShape(shapeName, 0, -1);
        });
      } else {
        if (this.selectedShapes.length > 0) {
          this.selectedShapes.forEach((shapeName) => {
            this.moveShape(shapeName.attrs.name, 0, -1);
          });
        } else {
          this.moveShape(this.selectedShapeId, 0, -1);
        }
      }
      this.isAnythingUpdated = true;
    },

    moveSelectedNodesdown() {
      if (this.isAllNodeSelected) {
        const selectedShapeNames = Object.keys(this.groups.rectangles);
        selectedShapeNames.forEach((shapeName) => {
          this.moveShape(shapeName, 0, 1);
        });
      } else {
        if (this.selectedShapes.length > 0) {
          this.selectedShapes.forEach((shapeName) => {
            this.moveShape(shapeName.attrs.name, 0, 1);
          });
        } else {
          this.moveShape(this.selectedShapeId, 0, 1);
        }
      }
      this.isAnythingUpdated = true;
    },

    moveSelectedNodesleft() {
      if (this.isAllNodeSelected) {
        const selectedShapeNames = Object.keys(this.groups.rectangles);
        selectedShapeNames.forEach((shapeName) => {
          this.moveShape(shapeName, -1, 0);
        });
      } else {
        if (this.selectedShapes.length > 0) {
          this.selectedShapes.forEach((shapeName) => {
            this.moveShape(shapeName.attrs.name, -1, 0);
          });
        } else {
          this.moveShape(this.selectedShapeId, -1, 0);
        }
      }
      this.isAnythingUpdated = true;
    },

    moveSelectedNodesright() {
      if (this.isAllNodeSelected) {
        const selectedShapeNames = Object.keys(this.groups.rectangles);
        selectedShapeNames.forEach((shapeName) => {
          this.moveShape(shapeName, 1, 0);
        });
      } else {
        if (this.selectedShapes.length > 0) {
          this.selectedShapes.forEach((shapeName) => {
            this.moveShape(shapeName.attrs.name, 1, 0);
          });
        } else {
          this.moveShape(this.selectedShapeId, 1, 0);
        }
      }
      this.isAnythingUpdated = true;
    },
    resetLayerScale() {
      this.layerScale = 1;
      this.isLayerDragging = false;
    },

    updateLayerBounds(pos) {
      const stage = this.$refs.stage.getStage();
      const scaleX = stage.width() * (1 - this.layerScale);
      const scaleY = stage.height() * (1 - this.layerScale);

      const x = Math.min(0, Math.max(pos.x, scaleX));
      const y = Math.min(0, Math.max(pos.y, scaleY));

      return {
        x,
        y,
      };
    },

    updateLayerZoom(value) {
      const layer = this.$refs.layer.getNode();
      layer.scale({
        x: value,
        y: value,
      });

      const pos = this.updateLayerBounds({ x: layer.x(), y: layer.y() });
      layer.x(pos.x);
      layer.y(pos.y);
    },

    handleWheel(e) {
      e.evt.preventDefault();
      this.isWheelScroll = true;
      const layer = this.$refs.layer.getNode();
      const oldScale = this.layerScale;

      const mousePointTo = {
        x:
          layer.getRelativePointerPosition().x / oldScale -
          layer.x() / oldScale,
        y:
          layer.getRelativePointerPosition().y / oldScale -
          layer.y() / oldScale,
      };

      let direction = e.evt.deltaY > 0 ? 1 : -1;
      // Zooming using trackpad, ctrlKey is always true
      if (!e?.evt?.ctrlKey) direction = -direction;
      else direction = -direction;
      const newScale =
        direction > 0 ? oldScale * this.scaleBy : oldScale / this.scaleBy;
      if (newScale <= 1) {
        this.resetLayerScale();
        return;
      }

      let _x =
        -(mousePointTo.x - layer.getRelativePointerPosition().x / newScale) *
        newScale;
      let _y =
        -(mousePointTo.y - layer.getRelativePointerPosition().y / newScale) *
        newScale;

      this.layerScale = newScale;
      const pos = this.updateLayerBounds({ x: _x, y: _y });
      layer.x(pos.x);
      layer.y(pos.y);
      this.isWheelScroll = false;
    },

    removeRuntimeObject() {
      if (this.newRect) {
        delete this.groups.rectangles[this.newRect.name];
        delete this.groups.labels[this.newRect.name];
        this.activeObjectArea = false;
        this.newRect = null;
        this.selectedShapeId = null;
        this.annotate = null;
      }
    },

    handleLayerMouseDown(e) {
      this.removeRuntimeObject();
      if (this.activeObjectArea && !this.newRect) {
        this.activeObjectArea = false;
      }
      if (this.drawFreeRectangle()) return;
      if (this.isLayerAtDefaultScale) {
        this.resetLayerScale();
        return;
      }
      this.activeObjectArea = false;
      this.isLayerDragging = true;
    },

    handleLayerMouseUp(e) {
      try {
        if (!this.isCompleteAnnotation(e)) return;
        this.showLabelPanel();
      } catch (error) {
        console.error(error);
      }
    },

    showLabelPanel() {
      if (this.isNewRectAdded && this.newRect) {
        this.selectedShapeId = this.newRect.name;
        this.handleFloatingBoatPosition(this.newRect);
        this.isNewRectAdded = false;
      }
    },

    isCompleteAnnotation(e) {
      const name = e.target?.name();
      if (!this.currentRectName || !name) return false;
      let position = {};
      if (name !== this.currentRectName) {
        position = this.getCurrentRectangleFromLayer() || {};
      }
      if (!Object.keys(position).length)
        position = {
          x: e.target?.x(),
          y: e.target?.y(),
        };

      this.updateRectPosition(position);
      this.resetObjectDrawing();
      this.isLayerDragging = false;
      this.isAnythingUpdated = true;
      // this.saveCurrentImageAnnotation(false, this.imageIndex);
      return true;
    },

    getCurrentRectangleFromLayer() {
      const layer = this.$refs.layer.getNode();
      if (!layer?.children?.length) return null;

      const group = layer.children.find(
        (group) => group.attrs.id === `${this.currentRectName + 1}`
      );
      if (!group) return null;

      const rect = group.children[0];
      return { x: rect?.attrs?.x, y: rect?.attrs?.y };
    },

    handleLayerFitWindow() {
      this.resetLayerScale();
    },

    shouldRemoveRectangle() {
      return !this.isDrawingRectangle() || this.shouldRemoveCurrentRectangle();
    },

    updateRectPosition(position) {
      if (this.shouldRemoveRectangle()) return;
      this.updateGroupItemsCoordinates(this.currentRectName, position);
      this.groups.labels[this.currentRectName].text = !this
        .isAnnotateObjectChecked
        ? ''
        : this.currentObjectName;
      if (!this.isAnnotateObjectChecked) {
        if (!this.annotateObjectStroke)
          this.annotateObjectStroke = this.assignStrokeColor();
        this.groups.rectangles[this.currentRectName].stroke =
          this.annotateObjectStroke;
      }
      if (this.isAnnotateObjectChecked) {
        const rectangle = this.groups.rectangles[this.currentRectName];
        this.groups.labels[this.currentRectName].text = this.currentObjectName;
        this.addObjectInLabeledList(rectangle);
      }
      this.isAnythingUpdated = true;
    },

    assignStrokeColor() {
      let color = getRandomColor();
      while (this.isStrokeExist(color)) color = getRandomColor();
      return color;
    },

    isStrokeExist(color) {
      for (const [key, value] of Object.entries(this.objectStrokeColor)) {
        if (value === color) return true;
      }
      return false;
    },

    shouldRemoveCurrentRectangle() {
      const rectangle = this.groups.rectangles[this.currentRectName];
      if (!rectangle) {
        this.deleteRectangle();
        return true;
      }
      if (rectangle.width !== 0 && rectangle.height !== 0) return false;
      this.deleteRectangle();
      return true;
    },

    deleteRectangle() {
      delete this.groups.rectangles[this.currentRectName];
      delete this.groups.labels[this.currentRectName];
      this.currentRectName = null;
      this.isAnythingUpdated = false;
    },

    debounce(func, wait) {
      return (...args) => {
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
        }
        this.debounceTimeout = setTimeout(() => {
          func.apply(this, args);
        }, wait);
      };
    },

    async updateStats() {
      await this.getTaskObjects(this.selectedTask);
    },

    async updateImageConfig() {
      const canvas = await this.getCanvasElement();
      const { width, height } = canvas.getBoundingClientRect();
      this.imageDimensions = {
        width,
        height,
      };
      this.stageSize = {
        width,
        height,
      };
    },

    setPrevShapes() {
      if (this.isHistoryApplied == false) {
        const currRects = this.groups.rectangles;
        if (Object.keys(currRects)?.length) {
          this.toast.error('Image is already annotated!');
          return;
        }
        if (!this.prevGroups?.length) {
          this.toast.error("There's no objects on previous image!");
          return;
        }
        this.resetInitState();
        this.updateGroups(this.prevGroups);
        this.isHistoryApplied = true;
        this.isAnythingUpdated = true;
      }
    },

    getCanvasElement() {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          const canvas = document.getElementsByTagName('canvas')[0];
          if (canvas) {
            clearInterval(interval);
            resolve(canvas);
          }
        }, 1000);
      });
    },

    isDraggable(object) {
      return OBJECT.isDraggable(object);
    },

    onObjectSelect(object) {
      const objName = object.name;
      this.selectedShapeName =
        this.selectedShapeName === objName ? null : objName;
      this.selectedShapeId = object.id ? null : object.id;
      this.isAddingRectangle = false;
      this.updateObjectTransformer();
    },

    updateCurrentObjectName(id) {
      this.currentObjectId = id;
      this.currentObjectName = this.getObjectName(id);
    },

    getObjectName(id) {
      const currentObj = this.annotateObjects.find((value) => value.id === id);
      if (currentObj) return currentObj.name;
      return null;
    },

    // add new object in groups
    setNewObjectConfigToDraw() {
      if (this.imageLoading || this.isObjectFetching || this.enableSegmentation)
        return;

      this.isAddingRectangle = !this.isAddingRectangle;
    },

    // get Image to Display
    async getImageUrlFromS3() {
      if (!this.imagePathInBucket) return;
      const obj = {
        bucket_name: this.organization + '-training',
        object_path: this.imagePathInBucket,
      };
      const { presigned_url } = await httpClient.post(
        'generic/generate_new_url/',
        obj,
        false,
        false,
        false
      );
      return presigned_url;
    },

    async loadImageRange(imageObj, image_index) {
      if (this.isCachingAnnotation) return; // Exit early if already caching
      this.handleDeselectAll(); // Deselect all items
      this.resetSegmentation();
      this.isCachingAnnotation = true; // Set caching state

      // Find the image index and update relevant states
      image_index = this.imageList.findIndex((val) => val.id === imageObj.id);
      this.imageIndexList = image_index;
      this.img_idx = image_index;

      const my_index = this.imageIndex;

      // Save the current image annotation before loading a new one
      await this.saveCurrentImageAnnotation(false, my_index);

      if (image_index !== my_index) {
        this.resetInitState(); // Reset only if the indices differ
      }
      if (
        !(image_index in this.imagePresignedUrl) &&
        !(image_index in this.annotationCache)
      ) {
        await this.cacheOnSelect(image_index);
      }

      // Load the image and cache the selection
      this.$emit('prevImage', image_index - this.imageIndex);

      this.isCachingAnnotation = false; // Reset the caching state

      // Handle applying previous labels if necessary
      if (this.img_idx > 0) {
        const prevAnnotation = this.annotationCache[this.img_idx - 1];
        if (!prevAnnotation || prevAnnotation.length === 0) {
          this.isApplyPreviousLabel = true; // Set flag to apply previous label
        }
      }
    },

    async loadImage() {
      if (!this.imagePresignedUrl[this.imageIndex]) {
        this.imageLoading = true;
        await this.cacheOnSelect(this.imageIndex);
        this.imageLoading = false;
        return;
      }
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.src = this.imagePresignedUrl[this.imageIndex];
        img.onload = () => {
          this.image = img;
          this.image.width = img.naturalWidth;
          this.image.height = img.naturalHeight;
          resolve(this.imagePresignedUrl[this.imageIndex]);
        };
        img.onerror = (err) => {
          console.error(err);
          resolve(0);
        };
      });
    },

    // for rendering existing objects
    getObjectListFromState() {
      return this.taskObjectList.map((o) => {
        return {
          ...o,
          cordinates_of_static_object: parseStringCoordsToObj(
            o.cordinates_of_static_object
          ),
        };
      });
    },

    async getAnnotationFromUrl(url, embeddings = false, spinner = false) {
      try {
        const config = {
          responseType: 'arraybuffer',
        };

        if (url) {
          if (spinner) store.dispatch('turnOnSpinner');
          const { data } = embeddings
            ? await axios.get(url, config)
            : await axios.get(url);
          store.dispatch('turnOffSpinner');
          return data;
        }
      } catch (error) {
        console.error(error);
        store.dispatch('turnOffSpinner');
        return null;
      }
    },
    async getObjectListFromS3() {
      try {
        let rectObjects = [];

        if (
          this.annotationCache[this.imageIndex] &&
          typeof this.annotationCache[this.imageIndex] === 'object' &&
          Object.keys(this.annotationCache[this.imageIndex]).length !== 0
        ) {
          const labeledAnnotations = this.annotationCache[
            this.imageIndex
          ]?.filter((o) =>
            this.nonStaticObjects.some((ns) => ns.id === o.actual_id)
          );
          rectObjects = labeledAnnotations
            ?.filter((o) => !o.hasOwnProperty('points'))
            ?.map(OBJECT.nestCoordinates);
        }

        return {
          rectObjects: rectObjects ? rectObjects : [],
        };
      } catch (error) {
        this.toast.error('Error fetching object list from S3: ' + error, 3000);
        return {
          rectObjects: [],
        };
      }
    },

    async renderExistingObjectList() {
      this.displayReviewInfoMessage = false;
      this.groups = { rectangles: {}, labels: {} };
      this.temp_groups = { rectangles: {}, labels: {} };
      const { rectObjects } = await this.getObjectListFromS3();
      this.updateGroups(rectObjects);
      this.createGroupClone();
      this.updateObjectTransformer();
      this.setAutoAnnotationItem();
      this.getMaskClicksAndPreviousMask();
      if (this.imageObj.requires_review) this.isAnythingUpdated = true;
      if (this.enableFastAnnotation) {
        await this.cacheCurrentImageEmbedding();
        await this.startFastAnnotation();
      }
    },

    setAutoAnnotationItem() {
      for (let key in this.groups.rectangles) {
        const rect = this.groups.rectangles[key];
        if (
          Object.prototype.hasOwnProperty.call(rect, 'auto_annotation_label_id')
        ) {
          this.aiAnnotationItem[rect.name] = rect.actual_id;
        }
      }
    },

    isAutoAnnotationLabelExist() {
      for (let key in this.groups.rectangles) {
        const rect = this.groups.rectangles[key];
        if (
          Object.prototype.hasOwnProperty.call(rect, 'auto_annotation_label_id')
        ) {
          return true;
        }
      }
      return false;
    },

    async getObjectListFromS3History(index) {
      try {
        let rectObjects = [];

        if (
          this.annotationCache[index] &&
          typeof this.annotationCache[index] === 'object' &&
          Object.keys(this.annotationCache[index]).length !== 0
        ) {
          const labeledAnnotations = this.annotationCache[index]?.filter((o) =>
            this.nonStaticObjects.some((ns) => ns.id === o.actual_id)
          );
          rectObjects = labeledAnnotations
            ?.filter((o) => !o.hasOwnProperty('points'))
            ?.map(OBJECT.nestCoordinates);
        }

        return {
          rectObjects: rectObjects ? rectObjects : [],
        };
      } catch (error) {
        this.toast.error('Error fetching object list from S3: ' + error, 3000);
        return {
          rectObjects: [],
        };
      }
    },

    async renderLastExistingObjectList() {
      if (
        this.imageLoading ||
        this.prevImageButtonDisabled ||
        this.isApplyPreviousLabel
      )
        return;
      this.groups = { rectangles: {}, labels: {} };
      this.temp_groups = { rectangles: {}, labels: {} };
      const { rectObjects } = await this.getObjectListFromS3History(
        this.imageIndex - 1
      );
      this.resetInitState();
      this.updateGroups(rectObjects);
      this.isAnythingUpdated = true;
    },

    selectObject(index) {
      this.$nextTick(() => {
        if (
          this.labeledObjectList.length &&
          index < this.labeledObjectList.length
        ) {
          this.selectedShapeName = this.labeledObjectList[index]?.name;

          this.updateObjectTransformer();
        }
      });
    },

    updateGroups(rectObjects) {
      if (!rectObjects.length) return;
      const s3Objects = rectObjects?.filter(
        (o) => !o.is_static && !o.hasOwnProperty('points')
      );
      const existingGroups = {
        ...this.createRectanglesFromExistingObjects([...s3Objects]),
      };
      this.setObjectColor(existingGroups.rectangles);
      this.groups = existingGroups;
      this.updateLabelSize();
      this.initializeLabeledObject();
    },

    updateRectProperties(key) {
      const rectangle = this.groups.rectangles[key];
      const label = this.groups.labels[key];
      const annotateObject = this.annotateObjects.find(
        (data) => data.id === rectangle.actual_id
      );
      if (annotateObject === undefined) return;
      if (isHandAndFaceObj(annotateObject.name, this.modelChoice)) {
        rectangle.stroke = this.darkRedColor;
        rectangle.is_label_visible = annotateObject.is_label_visible;
        rectangle.is_object_visible = annotateObject.is_object_visible;
        return;
      }
      rectangle.actual_name = annotateObject.name;
      rectangle.stroke = annotateObject.outline_color;
      rectangle.is_label_visible = annotateObject.is_label_visible;
      rectangle.is_object_visible = annotateObject.is_object_visible;
      label.text = rectangle.actual_name;
    },

    initializeLabeledObject() {
      this.labeledObjectList = [];
      this.isObjectFetching = true;
      for (const key of Object.keys(this.groups.rectangles)) {
        this.updateRectProperties(key);
        this.addObjectInLabeledList(this.groups.rectangles[key]);
      }
      this.isObjectFetching = false;
    },

    setObjectColor(objects) {
      Object.values(objects).forEach((o) => {
        const objName = o.actual_name ? o.actual_name : o.name;
        o['stroke'] = this.objectStrokeColor[objName];
      });
    },

    async uploadAnnotationToS3(
      s3Objects,
      hidetoast,
      imageIndex,
      emptyAnnotation = false
    ) {
      const currentImageId = this.imageId;
      const image_path = this.imagePathInBucket;
      this.uploadQueue = this.uploadQueue
        .then(async () => {
          try {
            const { file_path, file_name } = await this.getAnnotationNamePath(
              image_path
            );
            const payload = await this.getPayloadForUploading(
              s3Objects,
              file_path,
              file_name
            );

            const fileUploadResult = await DetectorService.uploadObjectFile(
              currentImageId,
              payload,
              false
            );
            if (fileUploadResult[1] == null) {
              this.toast.error('Failed to save annotations', 3000);
              hidetoast = true;
            }
            if (Object.keys(s3Objects).length != 0 || emptyAnnotation) {
              const imageAnnotationResult =
                await DetectorService.updateImageAnnotation(
                  {
                    path_to_annotations: file_path,
                    ai_annotation: false,
                    rotation:
                      this.taskParameters?.telemetryParams?.object_rotation,
                  },
                  currentImageId,
                  false
                );
            }
            const updatedAnnotation = {
              path_to_annotations: this.getAnnotationPathForState(file_path),
              rotation: this.taskParameters?.telemetryParams?.object_rotation,
            };
            this.$emit('updateAnnotationPath', updatedAnnotation);
          } catch (error) {
            this.toast.error('Failed to save annotations', 3000);
            hidetoast = true;
            console.error('Error in uploadAnnotationToS3:', error);
          }
        })
        .catch((error) => {
          console.error('Error in upload queue:', error);
        });
    },

    getAnnotationPathForState(file_path) {
      const rects = Object.keys(this.groups.rectangles);
      if (!rects.length) return null;
      else return file_path;
    },

    async getAnnotationNamePath(image_path) {
      const pathSplit = image_path.split('/');
      const imageFileName = pathSplit.at(-1);
      return {
        file_path: `${pathSplit
          .slice(0, 2)
          .join('/')}/labels/${imageFileName}.json`,
        file_name: `${imageFileName}.json`,
      };
    },

    getPayloadForUploading(s3Objects, file_path, file_name) {
      const fileAsJSON = JSON.stringify(s3Objects);
      const blob = new Blob([fileAsJSON], { type: 'application/json' });
      const data = new FormData();
      data.append('file', fileAsJSON);
      data.append('file_path', file_path);
      data.append('bucket', this.organization + '-training');
      return data;
    },

    changeLabelColor(isActive) {
      if (isActive) return;
      if (this.textColor === 'white') this.textColor = 'black';
      else this.textColor = 'white';
    },

    async preloadImageAndAnnotation() {
      let presigned_url = this.presignedUrlList;
      if (!this.presignedUrlList.hasOwnProperty('image')) return;
      if (Object.keys(this.presignedUrlList.image)[0] != this.startIndex) {
        presigned_url = await this.generateBulkImageS3Url(
          this.startIndex,
          this.endIndex,
          'annotation'
        );
      }
      this.imagePresignedUrl = presigned_url.image;
      this.annotationCache[this.imageIndex] = await this.getAnnotationFromUrl(
        presigned_url.annotation[this.imageIndex]
      );
      Object.keys(this.presignedUrlList.annotation)
        .filter((key) => key != this.imageIndex)
        .map((key) => {
          return this.getAnnotationFromUrl(
            this.presignedUrlList.annotation[key]
          ).then((result) => {
            this.annotationCache[key] = result;
          });
        });
    },

    cacheEmbeddings() {
      if (!this.enableFastAnnotation) return;

      Object.keys(this.presignedUrlList.embeddings)
        .filter((key) => key != this.imageIndex)
        .map((key) => {
          return this.getAnnotationFromUrl(
            this.presignedUrlList.embeddings[key],
            true
          ).then((result) => {
            this.embeddingsCache[key] = result;
          });
        });
    },

    async cacheImages(change_index) {
      if (this.isCachingImage) return; // Prevent concurrent operations
      this.isCachingImage = true;

      if (change_index > 0) {
        const image_list_length = Object.entries(this.imageList).length;
        await this.cacheForwardImages(image_list_length);
      } else if (change_index < 0) {
        await this.cacheBackwardImages();
      }

      this.isCachingImage = false;
    },

    async cacheForwardImages(image_list_length) {
      const image_keys = Object.keys(this.imagePresignedUrl);
      const length = image_keys.length;
      const lastIndex = Number(image_keys[length - 1]);
      if (lastIndex < image_list_length && lastIndex - this.imageIndex <= 5) {
        const start_index = lastIndex + 1;
        const end_index = Math.min(start_index + 11, image_list_length);
        if (start_index != end_index) {
          await this.fetchNewImages(start_index, end_index, true);
        }
      }
    },

    async cacheBackwardImages() {
      const image_keys = Object.keys(this.imagePresignedUrl);
      const first_index = Number(image_keys[0]);

      if (first_index > 0 && this.imageIndex - first_index <= 5) {
        const end_index = first_index;
        const start_index = Math.max(end_index - 10, 0);
        if (start_index != end_index) {
          await this.fetchNewImages(start_index, end_index, false);
        }
      }
    },

    cacheEmbeddingsFromKeys(presigned_url, object_keys) {
      if (!this.enableFastAnnotation) return;
      if (!presigned_url?.embeddings) return;
      this.embeddingsCache = {};
      object_keys.forEach((key) => {
        if (!presigned_url.embeddings[key]) {
          this.embeddingsCache[key] = null;
          return;
        }
        this.getAnnotationFromUrl(presigned_url.embeddings[key], true).then(
          (result) => {
            this.embeddingsCache[key] = result;
          }
        );
      });
    },

    async fetchNewImages(start_index, end_index, removeStart) {
      const presigned_url = await this.generateBulkImageS3Url(
        start_index,
        end_index
      );
      const object_keys = Object.keys(presigned_url.image);

      object_keys.forEach(async (key) => {
        this.imagePresignedUrl[key] = presigned_url.image[key];
        this.annotationCache[key] = await this.getAnnotationFromUrl(
          presigned_url.annotation[key]
        );
      });

      this.cacheEmbeddingsFromKeys(presigned_url, object_keys);
      this.$emit('updateCacheIndex', start_index, end_index);

      if (removeStart) {
        this.removeOldKeys('shift');
      } else {
        this.removeOldKeys('pop');
      }
    },

    removeOldKeys(method) {
      const image_keys = Object.keys(this.imagePresignedUrl);
      const annotation_keys = Object.keys(this.annotationCache);

      for (let i = 0; i < 10; i++) {
        delete this.imagePresignedUrl[image_keys[method]()];
        delete this.annotationCache[annotation_keys[method]()];
      }
    },

    async cacheOnSelect(index) {
      const image_list_length = Object.entries(this.imageList).length;
      const { start_index, end_index } = this.calculateIndexes(
        index,
        image_list_length
      );
      await this.updateCache(start_index, end_index);
    },

    calculateIndexes(imageIndex, image_list_length) {
      let start_index = imageIndex - 20;
      let end_index = imageIndex + 20;

      // Ensure start_index is not less than 0
      start_index = Math.max(start_index, 0);

      // Adjust end_index if start_index is at the beginning of the list
      if (start_index === 0) {
        end_index = Math.min(30, image_list_length); // Make sure it doesn't exceed the list length
      } else {
        // Ensure end_index is within bounds
        end_index = Math.min(end_index, image_list_length);

        // If the range between start and end is less than 30, adjust start_index accordingly
        if (end_index - start_index < 30) {
          start_index = Math.max(0, end_index - 30);
        }
      }

      return { start_index, end_index };
    },

    shouldUpdateCache(start_index, end_index, pre_image_keys) {
      return !(
        pre_image_keys.includes(start_index.toString()) &&
        pre_image_keys.includes(end_index.toString())
      );
    },

    async updateCache(start_index, end_index) {
      const presigned_url = await this.generateBulkImageS3Url(
        start_index,
        end_index
      );
      await this.applyPresignedUrls(presigned_url);
    },

    resetCaches() {
      this.imagePresignedUrl = {};
      this.annotationCache = {};
    },

    async applyPresignedUrls(presigned_url) {
      this.imagePresignedUrl = {};
      const object_keys = Object.keys(presigned_url.image);
      const image_keys = Object.keys(this.imagePresignedUrl);
      const annotation_keys = Object.keys(this.annotationCache);

      object_keys.forEach(async (key) => {
        this.imagePresignedUrl[key] = presigned_url.image[key];
        this.annotationCache[key] = await this.getAnnotationFromUrl(
          presigned_url.annotation[key]
        );
        if (image_keys.length)
          delete this.imagePresignedUrl[image_keys.shift()];
        if (annotation_keys.length)
          delete this.annotationCache[annotation_keys.shift()];
      });
      this.cacheEmbeddingsFromKeys(presigned_url, object_keys);
    },

    async getObjectListCacheFromS3(objectPath) {
      const obj = {
        bucket_name: this.organization + '-training',
        object_path: objectPath,
      };
      const res = await httpClient.post(
        'generic/generate_new_url/',
        obj,
        false,
        false,
        false
      );
      let { data } = await axios.get(res.presigned_url);

      return { data };
    },

    async generateImageS3Url(image_path) {
      if (!image_path) return;

      const obj = {
        bucket_name: this.organization + '-training',
        object_path: image_path,
      };

      try {
        const response = await httpClient.post(
          'generic/generate_new_url/',
          obj,
          false,
          false,
          false
        );
        const presigned_url = response; // Assuming response structure
        return presigned_url;
      } catch (error) {
        this.toast.error('Error generating presigned URL ' + error, 3000);
      }
    },

    async getImageUrlFromS3Index(image_path, image_index, imageObj) {
      const obj = {
        bucket_name: this.organization + '-training',
        object_path: image_path,
      };
      try {
        const response = await httpClient.post(
          'generic/generate_new_url/',
          obj,
          true,
          false,
          false
        );
        const presigned_url = response.presigned_url;
        return presigned_url;
      } catch (error) {
        this.toast.error('Error fetching presigned URL ' + error, 3000);
      }
    },

    async generateBulkImageS3Url(startIndex, endIndex, type) {
      const obj = {
        bucket_name: this.organization + '-training',
        task_id: this.selectedTask,
        start_index: startIndex,
        end_index: endIndex,
        filter: this.annotationFilterValues,
        ordering: this.sortingValue,
      };

      try {
        const response = await httpClient.post(
          'generic/generate_list_new_url/',
          obj,
          false,
          false,
          false
        );
        const presigned_url = response.presigned_url; // Assuming response structure

        return presigned_url;
      } catch (error) {
        this.toast.error('Error generating presigned URL ' + error, 3000);
      }
    },
    deleteNode(shapeName) {
      delete this.groups.rectangles[shapeName];
      delete this.groups.labels[shapeName];
      this.labeledObjectList = this.labeledObjectList.filter(
        (obj) => obj.name !== shapeName
      );
    },
    async deleteObject() {
      let index = 0;
      if (this.isAllNodeSelected) {
        const selectedShapeNames = Object.keys(this.groups.rectangles);

        if (selectedShapeNames.length === 0) return;

        for (const shapeName of selectedShapeNames) {
          if (!this.groups.rectangles[shapeName]) continue;
          this.deleteNode(shapeName);
        }
      } else {
        if (this.selectedShapes.length > 0) {
          this.selectedShapes.forEach((shapeName) => {
            this.deleteNode(shapeName.attrs.name);
          });
        } else {
          this.deleteNode(this.selectedShapeName);
        }
      }
      this.selectedShapeName = null;
      this.isAnythingUpdated = true;
      this.isAddingRectangle = false;
      this.deleteShow = false;

      this.trySelectNextShape(index); // Attempt to select the next shape, starting from the beginning
      this.updateObjectTransformer();
      this.handleDeselectAll();
      this.isAnythingUpdated = true;
    },

    trySelectNextShape(index) {
      if (index === -1) return;
      if (index && index - this.labeledObjectList.length === 0) index -= 1;
      if (index < this.labeledObjectList.length)
        this.selectedShapeName = this.labeledObjectList[index].name;
    },

    handleDeleteMenuShow(e) {
      e.evt?.preventDefault();
      if (!e.target.hasOwnProperty('parent')) {
        return;
      }
      const pos = this.$refs.stage.getStage().getPointerPosition();
      const selectedObjectName = this.getObjectTargetName(e);
      if (selectedObjectName === null) return;
      this.showDeleteContextMenu(pos, selectedObjectName);
    },

    getObjectTargetName(e) {
      let name = e.target?.name();
      if (name && name !== this.imageConfig.name) {
        return this.getSelectedObjectName(e);
      }
      return null;
    },

    getSelectedObjectName(e) {
      if (!e.target?.id() || !e.target?.name()) return null;
      const idText = e.target.id().split('-').slice(-1)[0];
      if (idText.toLowerCase() !== 'text') return e.target.name();
      const groupChildren = e.target.parent?.children;
      if (groupChildren?.length) {
        const rect = groupChildren[0];
        return rect?.attrs?.name;
      }
      return null;
    },

    showDeleteContextMenu(pos, name) {
      this.activeObjectArea = false;
      let deleteContextMenu = document.getElementById('deleteContextMenu');
      deleteContextMenu.style.top = `${pos.y + 10}px`;
      deleteContextMenu.style.left = `${pos.x - 25}px`;
      const rect = this.groups.rectangles[name];
      if (rect) {
        this.selectedShapeName = name;
        this.deleteShow = true;
      }
    },

    handleStageClick(e) {
      e.evt?.preventDefault();
      if (e.evt?.button === 2) return;
      const deleteContextMenu = document.getElementById('deleteContextMenu');
      if (deleteContextMenu.style.display === 'initial') {
        this.selectedShapeName = null;
        this.deleteShow = false;
      }
      if (e.evt) {
        if (!e.evt.ctrlKey) {
          this.handleDeselectAll();
        }
      }
    },

    resetObjectDrawing() {
      this.isDrawing = false;
      this.isAddingRectangle = false;
    },

    handleLayerMouseMove(e) {
      if (this.enableSegmentation || this.enableFastAnnotation) {
        this.isSegmentationHover = true;
        this.performSegmentation(e);
        return;
      }
      this.handleMouseMoveDrawRectangle();
    },

    handleMouseMoveDrawRectangle() {
      if (!this.isDrawingRectangle()) return;

      const { rectangles } = this.groups;
      const currentRect = rectangles[this.currentRectName];
      if (!currentRect) return;
      const point = this.$refs.layer.getNode().getRelativePointerPosition();
      currentRect.width = point.x - currentRect.startPointX;
      currentRect.height = point.y - currentRect.startPointY;
      if (!this.isAnnotateObjectChecked) {
        // this.handleFloatingBoatPosition(currentRect)
        this.newRect = currentRect;
        this.isNewRectAdded = true;
      }
    },

    handleFloatingBoatPosition(currentRect) {
      // Get the container element of the Konva stage
      const layerNode = this.$refs.layer.getNode();
      const container = layerNode.getStage().container();

      // Get the bounding rectangle of the container
      const layerRect = container.getBoundingClientRect();

      // Calculate default bottom-right point relative to the layer
      const defaultBottomRightX =
        currentRect.startPointX + currentRect.width + 50;
      const defaultBottomRightY =
        currentRect.startPointY + currentRect.height + 50;

      // Fixed dimensions of the floating card
      const floatingCardWidth = 200;
      const floatingCardHeight = 94;

      // Determine the final position of the floating card
      let finalX = defaultBottomRightX;
      let finalY = defaultBottomRightY;

      // Check if the floating card goes out of the right boundary
      if (defaultBottomRightX + floatingCardWidth > layerRect.width) {
        // Place the card at the top of the rectangle
        finalX =
          currentRect.startPointX + currentRect.width - floatingCardWidth;
      }

      // Check if the floating card goes out of the bottom boundary
      if (defaultBottomRightY + floatingCardHeight > layerRect.height) {
        // Place the card at the top of the rectangle
        finalY = currentRect.startPointY - floatingCardHeight;
      }

      // Position the floating card
      this.$refs.floatingCard.style.top = `${finalY}px`;
      this.$refs.floatingCard.style.left = `${finalX}px`;
      this.$refs.floatingCard.style.width = `${floatingCardWidth}px`;
      this.$refs.floatingCard.style.height = `fit-content`;
      this.$refs.floatingCard.style.zIndex = 900;
      this.deleteShow = false;
      this.activeObjectArea = true;
      this.searchValue = null;
      this.annotate = null;
      this.$nextTick(() => this.$refs.annotateSelect?.focus());
    },

    isDrawingRectangle() {
      return this.isDrawing;
    },

    drawFreeRectangle() {
      if (!this.isAddingRectangle || this.isDrawing) return;
      this.isDrawing = true;
      this.isLeftButtonPressed = true;
      this.initializeObject();
    },

    initializeObject(x = -1, y = -1, w = 0, h = 0) {
      const object = {
        cordinates_of_static_object: null,
        is_static: false,
        name: !this.isAnnotateObjectChecked ? '' : this.currentObjectName,
        actual_id: this.currentObjectId,
        id: uuid.v4(),
      };

      let { rectangle, label } = this.createRectLabelGroup(object);
      const pos = this.$refs.layer.getNode().getRelativePointerPosition();
      rectangle.startPointX = x == -1 ? pos.x : x;
      rectangle.startPointY = y == -1 ? pos.y : y;
      rectangle.actual_name = this.currentObjectName;
      rectangle.x = x;
      rectangle.y = y;
      rectangle.width = w;
      rectangle.height = h;
      label.text = null;
      label.fontSize = this.currentLabelSize;
      label.x = rectangle.startPointX;
      label.y =
        this.getLabelVerticalCoordinate(rectangle.name) ??
        this.getDefaultLabelCoordinate(rectangle.startPointY);
      rectangle.stroke = this.objectStrokeColor[this.currentObjectName];

      const newGroups = this.groups;
      this.currentRectName = rectangle.name;
      newGroups.rectangles[rectangle.name] = {
        ...rectangle,
      };
      if (!this.isAnnotateObjectChecked) {
        this.annotateObjectStroke = this.assignStrokeColor();
        newGroups.rectangles[rectangle.name].stroke = this.annotateObjectStroke;
      }
      newGroups.labels[rectangle.name] = { ...label };
      return true;
    },

    getDefaultLabelCoordinate(pointY) {
      return pointY - this.labelVerticalGap;
    },

    addObjectInLabeledList(rectangle) {
      if (
        rectangle.name == '' ||
        rectangle.actual_name == '' ||
        rectangle.id == ''
      )
        return;
      if (this.labeledObjectList.find((data) => data.id == rectangle.id)) {
        this.labeledObjectList = this.labeledObjectList.filter(
          (data) => data.id !== rectangle.id
        );
      }
      this.labeledObjectList.push({
        name: rectangle.name,
        actual_name: rectangle.actual_name,
        id: rectangle.id,
        actual_id: rectangle.actual_id,
        segment_propagation: rectangle?.segment_propagation || false,
      });
    },

    // set Transformer when click on stage
    handleStageMouseDown(e) {
      if (this.isDrawingRectangle()) return;

      if (!e.target.hasOwnProperty('parent')) {
        return;
      }

      const clickedOnTransformer =
        e.target.getParent()?.className === 'Transformer';
      if (clickedOnTransformer) {
        return;
      }

      const name = e.target.name();
      const rect = this.groups.rectangles[name];

      this.selectedShapeName = rect ? name : '';

      this.updateObjectTransformer();
    },

    async changeImage(index) {
      this.cacheImages(index);
      await this.saveCurrentImageAnnotation(false, this.imageIndex);
      this.resetInitState();
      this.img_idx += index;
      this.resetChangeImage(index);
      this.$emit('prevImage', index);
      if (
        this.annotationCache[this.img_idx - 1] == null ||
        this.annotationCache[this.img_idx - 1] === '' ||
        this.annotationCache[this.img_idx - 1].length === 0
      ) {
        this.isApplyPreviousLabel = true;
      }
    },

    resetSegmentation() {
      this.clicks = [];
      this.maskList = [];
      this.previousMaskData = [];
      this.maskImage = null;
      this.hoverMaskImage = null;
      this.enableSegmentation = false;
      this.isSegmentationHover = false;
      this.shouldPreserveSegmentation = false;
      for (const key of Object.keys(this.tensorEmbedding)) {
        this.tensorEmbedding[key] = null;
      }
    },

    clearCurrentMask() {
      this.clicks = [];
      this.hoverMaskImage = null;
    },

    resetChangeImage(index) {
      this.resetLayerScale();
      this.imageIndexList = this.imageIndexList + index;
      this.selectedShapeName = null;
      this.selectedShapeId = null;
      this.isAddingRectangle = false;
      this.updateObjectTransformer();
      this.handleDeselectAll();
    },

    resetInitState() {
      // cleanup
      this.resetTransform();
      this.resetSegmentation();
      this.groups = { rectangles: {}, labels: {} };
      this.deleteShow = false;
      this.activeObjectArea = false;
      this.isAddingRectangle = false;
      this.isHistoryApplied = false;
      this.isObjectFetching = false;
      this.isAnythingUpdated = false;
      this.annotate = null;
      this.searchValue = null;
      this.temp_groups = {
        rectangles: {},
        labels: {},
      };
      this.isDrawing = false;
      this.labeledObjectList = [];
      this.selectedShapeName = null;
      this.selectedShapeId = -1;
      this.isApplyPreviousLabel = false;
    },

    shouldUpdateFile() {
      return JSON.stringify(this.groups) !== JSON.stringify(this.temp_groups);
    },

    async saveCurrentImageAnnotation(
      hidetoast = false,
      imageIndex = this.imageIndex
    ) {
      if (this.newRect) {
        delete this.groups.rectangles[this.newRect.name];
        delete this.groups.labels[this.newRect.name];
        this.activeObjectArea = false;
        this.newRect = null;
        this.selectedShapeId = null;
        this.annotate = null;
        this.selectedShapeName = '';
      }
      this.resetObjectDrawing();
      if (
        !this.imageObj.requires_review &&
        (!this.shouldUpdateFile() || !this.isAnythingUpdated)
      ) {
        return;
      }
      if (!this.isAutoAnnotationLabelExist()) {
        const [error, _] = await DetectorService.updateImageAnnotation(
          { requires_review: false },
          this.imageId
        );
        if (!error)
          this.$emit('updateAnnotationPath', { requires_review: false });
      }
      const newAnnotations = this.getAnnotation();
      this.annotationCache[this.imageIndex] = newAnnotations;
      await this.uploadAnnotationToS3(newAnnotations, hidetoast, imageIndex);
      this.createGroupClone();
      this.isAnythingUpdated = false;
    },

    async saveEmptyAnnotation(hidetoast = false, imageIndex = this.imageIndex) {
      this.isAllNodeSelected = true;
      this.deleteObject();
      this.isAnythingUpdated = false;
      this.isAllNodeSelected = false;
      const newAnnotations = [];
      this.annotationCache[this.imageIndex] = newAnnotations;
      await this.uploadAnnotationToS3(
        newAnnotations,
        hidetoast,
        imageIndex,
        true
      );
    },

    createGroupClone() {
      this.temp_groups = deepClone(this.groups);
    },

    getAnnotation() {
      const tempAnnotationsGroups = deepClone(this.groups);
      const { rectangles, labels } = tempAnnotationsGroups;
      let annotationList = [];
      const isRotation =
        this.taskParameters?.telemetryParams?.object_rotation || false;
      for (const [name, rectangle] of Object.entries(rectangles)) {
        const serializedRectangle = this.serializeNodeToObject(
          name,
          true,
          isRotation
        );
        let annotationObject = {
          ...rectangle,
          ...serializedRectangle,
          name: labels[name].text,
        };
        delete annotationObject['segment_propagation'];
        delete this.groups.rectangles[name].segment_propagation;
        const labelObject = this.labeledObjectList.find(
          (obj) => obj.name === name
        );
        if (labelObject) labelObject.segment_propagation = false;
        this.roundObjectValuesToDecimal(annotationObject, 3);
        annotationList.push(annotationObject);
      }
      return annotationList.map(OBJECT.filterRectanglePropsFromObject);
    },

    roundObjectValuesToDecimal(object, round) {
      const keys = Object.keys(object);
      keys.forEach((key) => {
        if (typeof object[key] === 'number')
          object[key] = parseFloat(object[key].toFixed(round));
      });
    },

    normalizeDim(object, normX, normY) {
      object.x = object.x / normX;
      object.y = object.y / normY;
      object.width = object.width / normX;
      object.height = object.height / normY;
    },

    changeLabelSize(size) {
      this.currentLabelSize = size;
      this.updateLabelSize();
    },

    updateLabelSize() {
      const { labels } = this.groups;
      const newLabels = Object.entries(labels).reduce((res, [key, value]) => {
        res[key] = {
          ...value,
          fontSize: this.currentLabelSize,
          y: this.getLabelVerticalCoordinate(key) ?? value.y,
        };
        return res;
      }, {});
      this.groups['labels'] = newLabels;
    },

    isAiAnnotate(item) {
      for (let key of Object.keys(this.groups.rectangles)) {
        const rect = this.groups.rectangles[key];
        if (
          rect.name === item.name &&
          Object.prototype.hasOwnProperty.call(
            rect,
            'auto_annotation_label_id'
          ) &&
          rect.auto_annotation_label_id !== item.actual_id
        ) {
          const annotationObject = this.getAnnotationLabelObject(
            rect.auto_annotation_label_id
          );
          const itemObject = this.getAnnotationLabelObject(item.actual_id);
          if (
            !annotationObject.is_label_visible ||
            !annotationObject.is_object_visible ||
            !itemObject.is_label_visible ||
            !itemObject.is_object_visible
          ) {
            if (!this.displayReviewInfoMessage) {
              this.displayReviewInfoMessage = true;
              this.toast.info('Please enable objects to review Ai Annotated.');
            }
            return false;
          }
          return true;
        }
      }
      return false;
    },

    getAiAnnotateRectangle(item) {
      for (let key of Object.keys(this.groups.rectangles)) {
        const rectObj = this.groups.rectangles[key];
        if (
          rectObj.name === item.name &&
          Object.prototype.hasOwnProperty.call(
            rectObj,
            'auto_annotation_label_id'
          )
        ) {
          return rectObj;
        }
      }
      return null;
    },

    getAiAnnotationOptions(item) {
      let rect = this.getAiAnnotateRectangle(item);
      if (!rect) return [];
      const object = this.getAnnotationLabelObject(
        rect.auto_annotation_label_id
      );
      return [
        { label: rect.actual_name, value: rect.actual_id },
        { label: object.name, value: object.id },
      ];
    },

    async handleAiAnnotateItemChange(item) {
      const rect = this.getAiAnnotateRectangle(item);
      if (!rect) return;
      if (!this.aiAnnotationItem[item.name]) return;

      const object = this.getAnnotationLabelObject(
        this.aiAnnotationItem[item.name]
      );

      rect.actual_id = object.id;
      rect.actual_name = object.name;
      item.actual_id = object.id;
      item.actual_name = object.name;
      delete this.aiAnnotationItem[item.name];
      delete rect.auto_annotation_label_id;
      this.isAnythingUpdated = true;
      await this.saveCurrentImageAnnotation();
    },

    handleChangeAiAnnotationObject(item) {
      const object = this.getAnnotationLabelObject(
        this.aiAnnotationItem[item.name]
      );
      if (!object) return;
      this.groups.labels[item.name].text = object.name;
      this.groups.rectangles[item.name].stroke = object?.outline_color
        ? object.outline_color
        : this.darkRedColor;
    },

    updateObjectTransformer() {
      try {
        this.updateTransformerRotationHeight();
        this.updateTransformer();
      } catch (err) {
        console.error(err);
      }
    },

    async handleLayerClick(e) {
      if (this.hoverMaskImage === null) return;
      this.isSegmentationHover = false;
      const bbox = await this.performSegmentation(e);
      if (!this.clicks.length) return;
      const x = this.clicks[this.clicks.length - 1].x / this.modelSizeOffset;
      const y = this.clicks[this.clicks.length - 1].y / this.modelSizeOffset;
      if (this.enableSegmentation) {
        this.maskList.push({
          ...this.clicks[this.clicks.length - 1],
          x: x,
          y: y,
        });
        this.shouldPreserveSegmentation = true;
        return;
      }
      if (!Object.keys(bbox).length) return;
      this.initializeObject(
        bbox['x'] * this.stageSize.width,
        bbox['y'] * this.stageSize.height,
        bbox['width'] * this.stageSize.width,
        bbox['height'] * this.stageSize.height
      );
      const currentRect = this.groups.rectangles[this.currentRectName];
      this.newRect = currentRect;
      this.selectedShapeId = this.newRect.name;
      this.handleFloatingBoatPosition(this.newRect);
      this.clearCurrentMask();
    },
  },
};
</script>
<style scoped>
.card-title {
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0px;
}
.annotation-image-card {
  display: flex !important;
  flex-direction: row !important;
  margin: 0px !important;
}
.annotation-image-card :deep(.ant-card-head) {
  display: flex !important;
  flex-direction: column !important;
  width: 80px;
  padding: 0px;
  margin: 0px;
  overflow-y: auto;
  max-height: 75vh;
  border: none;
}

#annotation-object > .ant-card-cover {
  padding: 0px !important;
}

.annotation-image-card > .ant-card-cover > div > .konvajs-content {
  width: unset !important;
  height: unset !important;
  flex-grow: 1 !important;
  display: flex;
}

.annotation-image-card > .ant-card-cover > div > .konvajs-content > canvas {
  border-radius: 0px !important;
  flex-grow: 1;
  width: 100% !important;
  height: 100% !important;
}

.objects-list {
  /* flex-grow: 1; */
  display: flex;
  flex-direction: column;
  height: 35% !important;
}

.objects-list :deep(.ant-list-header) {
  background: lightgray !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.objects-list :deep(.ant-list-header span) {
  font-weight: 600 !important;
}

.objects-list :deep(.ant-list-header p) {
  margin: 0;
  font-size: 11px;
}

.objects-list :deep(.ant-spin-nested-loading) {
  flex-grow: 1;
  height: 1px;
  overflow-y: auto;
}

/* .entity-list-item:hover {
  background: rgb(231, 231, 231);
} */

.selected-object {
  background: rgb(241, 238, 238);
}

.color-box {
  width: 20px;
  height: 10px;
  display: inline-block;
  background: red;
}
</style>
